import React, { useRef, useEffect, useMemo } from "react";
import {
  Paper,
  Grid,
  Typography,
  Box,
  Divider,
  Button,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  useMediaQuery,
} from "@mui/material";
import {
  GetUserWalletByUserId,
  GetClientWalletCompanyBalance,
  GetPolicyWalletOutstanding,
  GetPolicyWalletBalance,
  GetUserFindbyId,
} from "../../../Service/_index";
import { checkUserType } from "../../../Shared/CommonConstant";
import { Formik, Form } from "formik";
import { useParams, useLocation } from "react-router-dom";
import Loader from "../../../UiComponents/Loader/Loader";
import AsyncIconButton from "../../../UiComponents/CommonLoader/AsyncIconButton";
import Styles from "../../../UiComponents/Scss/AddPopup.module.scss";
import { AddIcon } from "../../../Resources/Icons/icons";
import ViewOnlinePolicy from "./ViewOnlinePolicy";
import ViewOnlinePolicyErrorLogs from "./ViewOnlinePolicyErrorLogs";

// Import modular components
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import BasicPolicyDetailsStep from "./components/BasicPolicyDetailsStep";
// PremiumCalculationStep removed; premium now shown inline in Basic step
import CustomerDetailsStep from "./components/CustomerDetailsStep";
import PreviewStep from "./components/PreviewStep";
import FormNavigationButtons from "./components/FormNavigationButtons";
import PartStatusList from "./components/PartStatusList";
import RetryPolicyDialog from "./components/RetryPolicyDialog";
import { friendlyErrorLine } from "./utils/friendlyError";
import PolicyNumbersTip from "./components/PolicyNumbersTip";
import CompletedPartsNotice from "./components/CompletedPartsNotice";
import ResubmitPartsPanel from "./components/ResubmitPartsPanel";

// Import custom hooks
import { useOnlinePolicyData } from "./hooks/useOnlinePolicyData";
import { useLivePolicyList } from "./hooks/useLivePolicyList";
import { useOnlinePolicyForm } from "./hooks/useOnlinePolicyForm";
import { readListView, saveListView } from "./utils/listView";
import { useDebounce } from "./hooks/useDebounce";

// Import constants and utilities
import {
  policyInitialValues,
  customerDetailsInitialValues,
  formSteps,
} from "./constants/initialValues";
import { combinedValidationSchema } from "./utils/validationSchemas";
import { titleFromGender } from "./utils/proposerTitle";
import {
  parseMongoDate,
  getStatusColor,
  formatCreatedAt,
  formatCurrency,
} from "./utils/dateHelpers";
import { midpointIdv, resolveIdvRange } from "./utils/idvHelpers";
import { readStashedPrefill } from "./utils/prefillHandoff";
import { selectionFromPolicy, comboFromPolicy, tpVehicleOf } from "./utils/vehicleSelection";
import {
  normalizeRtoCode,
  savedSnapshotFromPolicy,
  calculationFromSavedPolicy,
  vehicleForPricing,
} from "./utils/masterDrift";
import {
  calculatePremium,
  filterVehicleData as filterVehicles,
} from "./utils/premiumCalculator";
import { vehicleData } from "./OptionsData";

import { ToastSuccess, ToastError } from "../../../UiComponents/Toaster/Toast";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import ReplayIcon from "@mui/icons-material/Replay";
import HistoryIcon from "@mui/icons-material/History";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RelianceStagesHoverCard, { hasStageView } from "./components/RelianceStagesHoverCard";
import StopRunDialog from "./components/StopRunDialog";
import StageStatusDialog from "./components/StageStatusDialog";
import TpPolicyDialog from "./components/TpPolicyDialog";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { SECONDARY_BUTTON_SX, PRIMARY_BUTTON_SX, STEPPER_SX, NAVY, FONT } from "./components/formStyles";
import "./components/onlinePolicyForm.scss";
import KycFilesDialog from "./components/KycFilesDialog";
import PolicyCardList from "./components/PolicyCardList";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import JobRecordingDialog from "./components/JobRecordingDialog";
import VideocamIcon from "@mui/icons-material/Videocam";
import FilterBox from "../../../UiComponents/FilterBox/FilterBox";
import {
  SUPPORTED_COMPANIES,
  walletRequirementsFor,
  motorPremiumExcludingPa,
  routingFromPolicy,
  splitFromPolicy,
  premiumSplit,
  chargeBasisOf,
  existingTpModeOfPolicy,
  tpDetailsOpen,
} from "./utils/companyRules";
import {
  hasRealParts,
  realParts,
  partsSignature,
  anyPartCompleted,
  unfinishedParts,
  allMotorPartsDone,
  joinNames,
  retryPlan,
  rowTone,
  ROW_TONES,
} from "./utils/policyParts";
import { frozenLegKeys, resubmitLabel, boughtAgainKeys } from "./utils/partChanges";
import {
  creatorOf,
  isEditedByAnotherPerson,
  normalizeChargeTo,
  payerClientId,
  refundCountsForPayer,
} from "./utils/editPayer";
import EditPayerChoice from "./components/EditPayerChoice";
import { CircularProgress } from "@mui/material";
import ConfirmBox from "../../../UiComponents/ConfirmBox/ConfirmBox";
import { createAttemptTracker } from "../../AddMoneyToUser/addMoneyUtils";
import { isPolicyPaymentHoldEnabled, isSuperAdminLogin } from "../../../Shared/policyPaymentHoldFlag";
import { isWaitingForMoney, requiredText } from "./utils/paymentHold";
import { useOnlinePolicyConfig } from "./hooks/useOnlinePolicyConfig";

// Where the "Colour rows by status" switch is remembered (this browser only).
const ROW_COLORS_KEY = "onlinePolicy.rowColors";

/**
 * The row action icons (the owner, 2026-10-27): one look in the app's navy - a tinted square that turns solid navy on
 * hover. Only "View issues" stays red, because it says something is wrong (the red-only-for-meaning rule).
 */
const ACTION_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  color: NAVY,
  backgroundColor: "#EEF3F9",
  border: "1px solid #D6E1EE",
  "&:hover": { backgroundColor: NAVY, borderColor: NAVY, color: "#fff" },
};
const ISSUE_ACTION_SX = {
  ...ACTION_SX,
  color: "#B42318",
  backgroundColor: "#FEF3F2",
  border: "1px solid #FECDCA",
  "&:hover": { backgroundColor: "#B42318", borderColor: "#B42318", color: "#fff" },
};

/**
 * One table cell's worth of text, with an optional caption underneath.
 *
 * MasterCommonTable normally wraps every cell in a Typography carrying
 * `.TableBodyCell` — `height: 20px !important; overflow: hidden` — which is
 * what sliced the Date and Make & Model cells in half. Declaring the columns
 * as `type: "component"` skips that wrapper (see `cellColumn` below), so the
 * sizing is ours to handle here.
 *
 * A long value is ellipsised at a fixed width and carries a tooltip with the
 * full text, so one customer with a very long name cannot stretch the row or
 * shove the Actions column off the right-hand edge.
 */
const TableTextCell = ({
  primary,
  secondary,
  maxWidth = 170,
  mono = false,
  title,
}) => {
  const text =
    primary === null || primary === undefined || primary === ""
      ? "-"
      : String(primary);

  return (
    <Tooltip
      title={title || [text, secondary].filter(Boolean).join(" · ")}
      placement="top"
    >
      <Box sx={{ maxWidth, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#1E3A5F",
            ...(mono && { fontFamily: "monospace" }),
          }}
        >
          {text}
        </Typography>
        {secondary ? (
          <Typography noWrap sx={{ fontSize: 11, color: "#667085" }}>
            {secondary}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
};

/**
 * Column that renders a pre-built element straight from the row.
 *
 * "component" is MasterCommonTable's own escape hatch from the fixed-height
 * `.TableBodyCell` wrapper — the one rule that cannot be relaxed globally,
 * since every other table in the app depends on it.
 */
const cellColumn = (id, label) => ({
  id,
  label,
  type: "component",
  renderComponent: (row) => row[id],
});

/**
 * Main Online Policy Component (Refactored)
 * Handles creation, editing, and viewing of online policies
 */
const OnlinePolicy = () => {
  const formRef = useRef();
  const { policyId } = useParams();
  const location = useLocation();
  console.log(policyId, "policyId from URL");

  // Determine which view mode based on URL path
  const isErrorLogsView = location.pathname.includes("/view/error_logs/");
  const isPolicyDetailsView = location.pathname.includes("/view/form/");

  // Custom hooks for data management
  const {
    openLoader,
    setOpenLoader,
    tpData,
    tableData,
    filteredVehicleData,
    setFilteredVehicleData,
    loadAllOnlinePolicies,
    fetchPolicyById,
    fetchPolicyByIdSilent,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
    handleChangePage,
    handleChangeRowsPerPage,
    retryOnlinePolicy,
    setPolicyHold,
    patchTableRow,
    patchRowFromResponse,
    companyFilter,
    setCompanyFilter,
    dateRange,
    setDateRange,
    sort,
    setSort,
    resetFilters,
  } = useOnlinePolicyData();

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  // Phones and tablets show the list as cards (below 900px the table only scrolled sideways, and not to its end)
  const isPhone = useMediaQuery("(max-width:899.95px)");

  // Flag to track initial mount
  const [isInitialMount, setIsInitialMount] = React.useState(true);

  // What this policy will take from which Company Wallet(s) — up to two
  // entries: motor premium always from the policy's own insurer, PA Cover
  // (when on) additionally from either that same insurer or Brisk. Each
  // entry: { company, amount, labels: [...], balance, sufficient }.
  const [walletChecks, setWalletChecks] = React.useState([]);

  // One Create = one policy (a double click made two): a request id per Create attempt — kept only when the server
  // did not answer — and a guard + loader while the request is on its way.
  const createTracker = useRef(createAttemptTracker());
  const sendingRef = useRef(false);
  const [creating, setCreating] = React.useState(false);
  // "Hold until the wallet has money" (REACT_APP_POLICY_PAYMENT_HOLD_ENABLED): on a Create the server decides what is
  // short and the page offers Cancel / Hold; Remove hold of such a policy confirms what it will charge.
  const holdEnabled = isPolicyPaymentHoldEnabled();
  const superAdmin = isSuperAdminLogin();
  const [holdOffer, setHoldOffer] = React.useState(null); // { message, customerData, policyData }
  const [chargeConfirm, setChargeConfirm] = React.useState(null); // the row whose hold is being removed
  const [stopConfirm, setStopConfirm] = React.useState(null); // the RUNNING policy whose Hold asks "stop the automation?"
  // Edit on a Reliance / KSHEMA row asks what to change: { anchor, policy } (the policy as just read back)
  const [editMenu, setEditMenu] = React.useState(null);
  const [kycFilesFor, setKycFilesFor] = React.useState(null); // Edit -> KYC files
  const [statusFor, setStatusFor] = React.useState(null); // Edit -> Status
  const [tpPolicyFor, setTpPolicyFor] = React.useState(null); // Edit -> TP policy details (two insurers)

  // EDIT MODE ONLY: cache of what a policy already has outstanding, per
  // company, keyed by editPolicyId (defined below) — see getEditOutstanding()
  // below. A ref, not state: it must be readable SYNCHRONOUSLY-AFTER-AWAIT
  // inside fetchWalletChecks without depending on a second effect's timing (a
  // separate state+effect pair here previously raced fetchWalletChecks —
  // the balance check could run before that effect's fetch resolved, using
  // a stale empty map and showing a false "insufficient").
  const editOutstandingCacheRef = useRef({}); // { [editPolicyId]: { companyName: amount } }

  // EDIT MODE: who pays for this edit ("creator" | "editor", asked when somebody other than the creator edits) and
  // the bought parts the editor ticked to buy AGAIN (a new purchase: the full new amount is charged once more).
  const [chargeTo, setChargeToState] = React.useState("creator");
  const [boughtAgain, setBoughtAgain] = React.useState([]);
  const [creatorName, setCreatorName] = React.useState("");


  // Server switches this screen mirrors (multi-company wallet split, PA options).
  const { multiProductPolicy } = useOnlinePolicyConfig();

  // Custom hook for form state management
  const {
    showForm,
    setShowForm,
    selectedMakeModel,
    setSelectedMakeModel,
    selectedCombo,
    setSelectedCombo,
    calculationResult,
    setCalculationResult,
    showCalculationResult,
    setShowCalculationResult,
    policyData,
    setPolicyData,
    activeStep,
    isEditMode,
    setIsEditMode,
    isViewMode,
    setViewMode,
    editPolicyData,
    setEditPolicyData,
    savedSnapshot,
    setSavedSnapshot,
    releaseSaved,
    handleNext,
    handleBack,
    handleReset,
    handleViewPolicyErrorLogs,
    handleViewPolicyHistory,
    handleViewPolicy,
    saveCustomerDetails: saveCustomerDetailsHook,
  } = useOnlinePolicyForm(loadAllOnlinePolicies);

  // The id that actually identifies "this policy" for wallet purposes —
  // matches the fallback chain useOnlinePolicyForm.js uses to build
  // `rerunId` at save time exactly, because that's the id the backend
  // writes/reads ledger rows against. `policyId` (the URL param) is only
  // populated on the /view/form/:policyId and /view/error_logs/:policyId
  // routes; the normal in-place edit flow (handleEditPolicy) never
  // navigates the URL, it just sets editPolicyData in state — so relying on
  // the URL param alone left this undefined during a real edit, silently
  // short-circuiting the outstanding-refund lookup to {} every time.
  const editPolicyId =
    editPolicyData?._id ||
    editPolicyData?.id ||
    editPolicyData?.policyId ||
    policyId;

  // Somebody other than the creator is editing: they choose who pays (EditPayerChoice).
  const showPayerChoice =
    isEditMode && isEditedByAnotherPerson(editPolicyData, sessionStorage.getItem("UserId"));

  // A new edit starts from the defaults: the creator pays, nothing bought is bought again.
  useEffect(() => {
    setChargeToState("creator");
    setBoughtAgain([]);
  }, [editPolicyId]);

  // The creator's name for the question (best effort; the question reads fine without it).
  useEffect(() => {
    setCreatorName("");
    const creatorUserId = showPayerChoice ? creatorOf(editPolicyData).userId : "";
    if (!creatorUserId) return undefined;
    let current = true;
    Promise.resolve(GetUserFindbyId(creatorUserId))
      .then((res) => {
        if (current) setCreatorName(res?.data?.name || "");
      })
      .catch(() => {});
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPolicyId, showPayerChoice]);

  // The balance check follows the choices: whose wallet pays, and which bought parts are bought again.
  useEffect(() => {
    if (isEditMode && policyData) fetchWalletChecks(policyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeTo, boughtAgain.join(",")]);

  /**
   * What this policy already has outstanding, per company — the backend's
   * edit flow refunds this before re-debiting from the edited data (see
   * COMPANY_WALLET_README.md "Editing a policy"), so the balance check needs
   * to add it back onto the current balance rather than comparing against a
   * raw balance that doesn't yet reflect the refund that's about to happen.
   *
   * Awaited directly inside fetchWalletChecks rather than populated by a
   * separate effect — fetching it THERE, on demand, is what guarantees it's
   * resolved before it's used, for every call site, regardless of render
   * timing. Cached per editPolicyId in a ref so repeat checks during the
   * same edit session (Preview open, dialog open, Create click, ...) don't
   * refetch — this data only changes when the policy is actually saved.
   */
  const getEditOutstanding = async () => {
    if (!isEditMode || !editPolicyId) return {};

    const cached = editOutstandingCacheRef.current[editPolicyId];
    if (cached) return cached;

    // What the policy has paid sits in the wallet of whoever paid for it (the creator's client), which is
    // usually the editor's own client too.
    const clientId = creatorOf(editPolicyData).clientId || sessionStorage.getItem("clientID");
    if (!clientId) return {};

    const res = await GetPolicyWalletOutstanding(clientId, editPolicyId);
    const map = {};
    (res?.data || []).forEach((o) => {
      map[o.companyName] = o.amount;
    });
    editOutstandingCacheRef.current[editPolicyId] = map;
    return map;
  };

  // Effect to load initial data
  useEffect(() => {
    if (isInitialMount) {
      // the search kept from before History / Edit (utils/listView.js) is part of the first load too
      loadAllOnlinePolicies(searchQuery ? { search: searchQuery } : null, page, rowsPerPage);
      setIsInitialMount(false);
    }
  }, []);

  /**
   * What this policy owes and to which Company Wallet(s) — the same resolution
   * as the backend's applyPolicyWalletDebits (shared/policySettings.js), so the
   * balance check shows what will really be taken:
   *   - one insurer (or the multi-company flow off): motor premium from the
   *     policy's own insurer, PA Cover (GST-inclusive) from that insurer or Brisk;
   *   - OD and TP with different insurers and the multi-company flow on: the OD
   *     share from the OD insurer, the TP share from the TP insurer, PA from
   *     the PA Cover Company.
   * Entries that hit the same wallet are merged into one, exactly like the
   * backend's accumulated debits.
   *
   * The calculator's finalPremium already contains PA (folded into Total B), so
   * PA is taken back out to get the motor premium — see motorPremiumExcludingPa.
   */
  const resolveRequiredByCompany = (data, paymentMethod = null) => {
    const motorPremium =
      motorPremiumExcludingPa(calculationResult?.finalPremium, data) ??
      Number(data?.originalFinalPremium ?? data?.finalPremium ?? 0);
    return walletRequirementsFor(
      {
        ...data,
        motorPremium,
        premiumCalculation: {
          odPremium: calculationResult?.totalAodPremium,
          // Total B carries PA as "PA to Owner Driver"; the TP net excludes it.
          tpPremium:
            calculationResult?.totalBodPremium != null
              ? (Number(calculationResult.totalBodPremium) || 0) -
                (Number(calculationResult.pAOwnerDriver) || 0)
              : undefined,
        },
      },
      {
        multiProduct: multiProductPolicy,
        excludeLegs: frozenLegs({ forCharge: true }),
        onlyBriskPa: paymentMethod === "link",
        // A two-company policy is debited the calculator's own (net) figures; an edit keeps the basis the
        // policy was sold with, so the balance check shows what the server will really take.
        chargeBasis: isEditMode ? chargeBasisOf(editPolicyData) : "net",
      }
    );
  };

  /**
   * EDIT MODE: wallet legs (od / tp / motor / pa) of parts already bought. The
   * server freezes them on an edit — nothing is refunded and nothing re-debited
   * — so the balance check leaves them out on both sides.
   */
  const frozenLegs = ({ forCharge = false } = {}) =>
    isEditMode
      ? frozenLegKeys(editPolicyData?.parts, {
          paymentLink: isPaymentLink(editPolicyData),
          // The new CHARGE includes the bought parts being bought again; the REFUND side still leaves every
          // bought leg alone (their old payment stays).
          boughtAgain: forCharge ? boughtAgain : [],
        })
      : [];

  /** The refund side of the same rule: bought legs are not given back. */
  const withoutFrozenRefunds = (outstanding) => {
    const frozen = frozenLegs();
    if (!frozen.length) return outstanding;
    const saved = editPolicyData || {};
    const legs =
      splitFromPolicy(saved)?.legs ||
      premiumSplit(
        {
          ...saved,
          motorPremium: saved.originalFinalPremium,
          odNet: saved.premiumCalculation?.odPremium,
          tpNet: saved.premiumCalculation?.tpPremium,
        },
        { multiProduct: multiProductPolicy, chargeBasis: chargeBasisOf(saved) }
      ).legs;
    const adjusted = { ...outstanding };
    legs
      .filter((l) => frozen.includes(l.key))
      .forEach((l) => {
        const name = Object.keys(adjusted).find(
          (c) => c.toLowerCase() === String(l.company || "").toLowerCase()
        );
        if (name) {
          adjusted[name] = Math.max(
            0,
            // `charge` is what was debited for the leg; a policy saved before it existed has only `total`.
            Number((adjusted[name] - (Number(l.charge ?? l.total) || 0)).toFixed(2))
          );
        }
      });
    return adjusted;
  };

  /**
   * Fetches the live balance for each wallet resolveRequiredByCompany says
   * this policy needs, and returns whether every one of them is sufficient.
   * A courtesy check only — see the backend note in COMPANY_WALLET_README.md;
   * the atomic compare-and-swap at creation time is the real authority.
   */
  const fetchWalletChecks = async (data, paymentMethod = null) => {
    const required = resolveRequiredByCompany(data, paymentMethod);
    // The wallet that pays: the creator's client by default, the editor's own when the editor chose to pay.
    const editorClientId = sessionStorage.getItem("clientID");
    const clientId = isEditMode
      ? payerClientId({ chargeTo, policy: editPolicyData, editorClientId })
      : editorClientId;

    // Resolved BEFORE the balance checks run (not read from a separately-
    // populated state) so it's always available in time — see
    // getEditOutstanding's own comment for why that matters.
    // The refund only helps the payer's balance when the payer's wallet is the one that paid.
    const outstanding =
      isEditMode && !refundCountsForPayer({ chargeTo, policy: editPolicyData, editorClientId })
        ? {}
        : withoutFrozenRefunds(await getEditOutstanding());

    const results = await Promise.all(
      required.map(async (r) => {
        // In edit mode, this policy's own currently-outstanding debit for
        // this company will be REFUNDED first by the backend before it
        // re-debits from the edited data — see COMPANY_WALLET_README.md
        // "Editing a policy". `delta` is what's actually needed ON TOP of
        // that refund — the number the user actually cares about ("only the
        // increase, not the full amount again"). delta <= 0 means this edit
        // is a net reduction: the refund alone already covers the new
        // amount, sufficient regardless of the current balance.
        const outstandingRefund = Number(outstanding[r.company] || 0);
        const delta = Number((r.amount - outstandingRefund).toFixed(2));

        if (!r.company || !clientId) {
          return { ...r, balance: 0, outstandingRefund, delta, sufficient: false };
        }
        try {
          const res = await GetClientWalletCompanyBalance(clientId, r.company);
          const balance = Number(res?.data?.balance) || 0;
          const sufficient = delta <= 0 || balance >= delta;
          return { ...r, balance, outstandingRefund, delta, sufficient };
        } catch (error) {
          console.error("Error fetching company wallet balance:", error);
          return { ...r, balance: 0, outstandingRefund, delta, sufficient: false };
        }
      })
    );

    setWalletChecks(results);
    return results;
  };

  /**
   * Human-readable summary of one wallet check, for the Preview box and the
   * Payment Method dialog. In edit mode, leads with the INCREMENT this edit
   * actually needs (`delta`), not the full resolved amount (`r.amount`) —
   * the backend refunds what this policy already has outstanding before
   * re-debiting, so the full amount was never what's actually being asked
   * for again. Create mode has no prior debit to net against (`delta` always
   * equals `r.amount` there), so this collapses to the plain "needs ₹X"
   * wording unchanged from before.
   */
  const describeWalletCheck = (r) => {
    const labels = r.labels.join(" + ");
    if (!isEditMode) {
      return r.sufficient
        ? `Sufficient for ${labels}`
        : `Insufficient for ${labels} — needs ₹${r.amount}`;
    }
    if (r.delta <= 0) {
      return `This edit reduces the amount charged for ${labels} — no additional balance needed`;
    }
    if (r.sufficient) {
      return `Sufficient — this edit needs ₹${r.delta} more for ${labels}`;
    }
    const shortfall = Number((r.delta - (r.balance || 0)).toFixed(2));
    return `Increase of ₹${r.delta} needed for ${labels} — current balance ₹${r.balance} is ₹${shortfall} short`;
  };

  // Open the create form pre-filled when navigated from the Make & Model page.
  //
  // Two ways in:
  //   * in-app navigation carries the payload in React Router's location.state
  //     (MakeModel.js -> CreatePolicyFromRow);
  //   * the IDV calculator's "Create Policy" opens a NEW TAB, which starts with
  //     a history of its own and so cannot be handed state — its payload comes
  //     through localStorage under the one-shot token named in ?prefill.
  useEffect(() => {
    const token = new URLSearchParams(location.search).get("prefill");
    const prefill =
      location.state?.makeModelPrefill || readStashedPrefill(token);

    // Drop the token from the address bar once used. readStashedPrefill has
    // already deleted the payload, so a refresh would prefill nothing anyway —
    // this just stops the stale token being bookmarked or shared.
    if (token) {
      window.history.replaceState({}, "", location.pathname);
    }

    if (!prefill) return;

    setShowForm(true);
    setSelectedCombo(null);
    setSelectedMakeModel({
      _id: prefill._id,
      make: prefill.make,
      model: prefill.model,
      idvRangeFrom: prefill.idvRangeFrom,
      idvRangeTo: prefill.idvRangeTo,
      cc: prefill.cc,
      depreciationPercent: prefill.depreciationPercent,
      company: prefill.company,
      discount: prefill.discount,
      // Needed by resolveIdvRange — the range is derived from this.
      manufacturerSellingPrice: prefill.manufacturerSellingPrice,
      modelNotFound: !!prefill.modelNotFound,
      nilDipPrice: prefill.nilDipPrice,
      tppdRequired: !!prefill.tppdRequired,
      paCoverMinAmount: prefill.paCoverMinAmount,
      vehicleVariant: prefill.vehicleVariant || "",
      label: `${prefill.make} - ${prefill.model}`,
      value: `${prefill.make} - ${prefill.model}`,
    });

    // Wait for the form to mount, then fill the dependent fields
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.setFieldValue("makeModel", prefill._id || "");
        // Company routing — a Make & Model row is one company's, so OD and TP are
        // both that company; PA Cover left on its Brisk default. Same derivation
        // as BasicPolicyDetailsStep's writeRouting on the manual-selection path:
        // settings first, every flat company field from it.
        {
          const routing = routingFromPolicy({
            company: prefill.company || "",
            odCompany: prefill.company || "",
            tpCompany: prefill.company || "",
            paCover: formRef.current.values?.paCover,
            paCoverCompany: formRef.current.values?.paCoverCompany || "Brisk",
          });
          if (!routing.settings.PACompany) routing.settings.PACompany = "Brisk";
          Object.entries(routing).forEach(([field, value]) =>
            formRef.current.setFieldValue(field, value)
          );
        }
        // Master value only — BasicPolicyDetailsStep's sync effect replaces it
        // with the logged-in user's own discount for this make/model once the
        // user's assignments have loaded.
        formRef.current.setFieldValue(
          "discount",
          prefill.discount !== undefined && prefill.discount !== null
            ? String(prefill.discount)
            : ""
        );
        formRef.current.setFieldValue(
          "vehicleVariant",
          prefill.vehicleVariant || ""
        );
        // Never set on this path. selectedMakeModel is handed the matching
        // _id above, so the sync effect in BasicPolicyDetailsStep saw no
        // mismatch and its model-not-found branch never ran — a National
        // vehicle flagged on the master reached the queue with no selling
        // price, which national.js treats as a hard failure.
        formRef.current.setFieldValue(
          "modelNotFound",
          !!prefill.modelNotFound
        );
        formRef.current.setFieldValue(
          "manufacturerSellingPrice",
          prefill.manufacturerSellingPrice || ""
        );
        // Auto-fill IDV with the MIDDLE of the range — same rule as picking
        // the make/model from the dropdown (BasicPolicyDetailsStep).
        //
        // MUST go through resolveIdvRange, not the raw stored fields: with a
        // selling price on the master the range is derived from it, and using
        // the stored figures here produced an IDV outside the very range the
        // form then validated against.
        const prefillRange = resolveIdvRange(prefill);
        if (prefillRange.from || prefillRange.to) {
          formRef.current.setFieldValue(
            "idv",
            midpointIdv(prefillRange.from, prefillRange.to)
          );
          formRef.current.setFieldValue("idvRangeFrom", prefillRange.from);
          formRef.current.setFieldValue("idvRangeTo", prefillRange.to);
        }
      }
    }, 100);
  }, []);

  // Effect to trigger search when debounced value changes
  useEffect(() => {
    if (isInitialMount) return; // Skip on initial mount

    const searchPayload = debouncedSearchQuery
      ? { search: debouncedSearchQuery }
      : null;

    // Reset to first page when search changes
    setPage(0);
    loadAllOnlinePolicies(searchPayload, 0, rowsPerPage);
  }, [debouncedSearchQuery]);

  // Refresh the wallet balance(s) whenever the Preview step opens
  useEffect(() => {
    if (activeStep === 2) {
      fetchWalletChecks(policyData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Effect to reload data when pagination changes
  useEffect(() => {
    if (isInitialMount) return; // Skip on initial mount

    const searchPayload = debouncedSearchQuery
      ? { search: debouncedSearchQuery }
      : null;
    loadAllOnlinePolicies(searchPayload, page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Sort is applied by the SERVER, so a header click means a re-query rather
  // than a re-render. Always from page 1: staying on page 4 of a result set
  // that just shrank to two pages shows an empty table.
  //
  // The company and date filters are deliberately NOT here — they are applied
  // by the Search button (handleSaveAllFilter), not as they are touched.
  useEffect(() => {
    if (isInitialMount) return;

    const searchPayload = debouncedSearchQuery
      ? { search: debouncedSearchQuery }
      : null;
    setPage(0);
    loadAllOnlinePolicies(searchPayload, 0, rowsPerPage, {
      company: companyFilter,
      dateRange,
      sort,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  // Filter vehicle data based on search input
  const filterVehicleData = (searchTerm) => {
    const filtered = filterVehicles(searchTerm, vehicleData);
    setFilteredVehicleData(filtered);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Live policy updates (2026-10-22): rows patch themselves, or the page reloads silently, when the server says a policy
  // changed. Only on the list itself - not while a form or the View page is open in this component.
  useLivePolicyList({
    enabled: !isPolicyDetailsView && !location.pathname.includes("/edit") && !location.pathname.includes("/error_logs") && !showForm,
    tableData,
    page,
    fetchPolicyByIdSilent,
    patchTableRow,
    reload: () =>
      loadAllOnlinePolicies(debouncedSearchQuery ? { search: debouncedSearchQuery } : null, page, rowsPerPage, { silent: true }),
  });

  // Handle manual refresh
  const handleRefresh = () => {
    const searchPayload = searchQuery ? { search: searchQuery } : null;
    loadAllOnlinePolicies(searchPayload, page, rowsPerPage);
  };

  // Collapsed by default — the filter panel is a heavy strip of controls and
  // most visits to this page are "look at the list", not "narrow the list".
  // ...but open again when it was open before History / Edit (utils/listView.js).
  const [showFilter, setShowFilterState] = React.useState(() => readListView().showFilter === true);
  const setShowFilter = (open) => {
    setShowFilterState(open);
    saveListView({ showFilter: !!open });
  };

  // "Colour rows by status" in the filter panel: green = bought, yellow = part bought, red = failed, violet = on hold
  // (rowTone).
  // Display only, so it applies at once rather than on Search; remembered in this browser.
  const [showRowColors, setShowRowColors] = React.useState(() => {
    try {
      return localStorage.getItem(ROW_COLORS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleRowColors = (on) => {
    setShowRowColors(on);
    try {
      localStorage.setItem(ROW_COLORS_KEY, on ? "1" : "0");
    } catch {
      // private window / blocked storage — the switch still works for this visit
    }
  };
  const rowToneOf = (row) => ROW_TONES[rowTone(row)] || null;
  const rowToneSx = (row) => {
    const tone = rowToneOf(row);
    return tone
      ? {
          backgroundColor: tone.bg,
          "&.MuiTableRow-hover:hover": { backgroundColor: tone.hover },
          "& > td:first-of-type": { boxShadow: `inset 4px 0 0 ${tone.bar}` },
        }
      : null;
  };

  /**
   * DRAFT filter values — what the panel's controls are bound to.
   *
   * Picking a company or a date range only edits these. Nothing is queried
   * until Search is pressed, which copies the draft onto the applied filters
   * (companyFilter / dateRange in the data hook). Two filters that each fired
   * their own request as they were touched meant choosing a company and then a
   * date range cost two full queries, and the list flickered through a state
   * the user never asked to see.
   */
  const [draftCompany, setDraftCompany] = React.useState(null);
  const [draftDateRange, setDraftDateRange] = React.useState(null);

  // Mirrors of the two above, read by handleSaveAllFilter.
  //
  // FilterBox calls handleSaveAllFilter from inside a setTimeout when a filter
  // is DESELECTED in manual mode, and that timeout holds the handler captured
  // at click time — whose closure still sees the value being cleared. Reading
  // through a ref (stable identity, current value) applies what the panel
  // actually shows rather than what it showed a moment ago.
  const draftCompanyRef = useRef(null);
  const draftDateRangeRef = useRef(null);
  draftCompanyRef.current = draftCompany;
  draftDateRangeRef.current = draftDateRange;

  // Bumping this remounts FilterBox, which is the only way to clear the
  // selections it keeps in its OWN state — it exposes no reset, and its
  // internal Clear All is hidden here (see the customActionButtons note below).
  // On remount it rebuilds itself from filterData, which by then holds the
  // reset values.
  const [filterBoxKey, setFilterBoxKey] = React.useState(0);

  // Applied -> draft. Runs on mount (so the panel opens showing this month, the
  // default that is already applied) and after Clear All resets them.
  useEffect(() => {
    setDraftCompany(companyFilter);
    setDraftDateRange(dateRange);
  }, [companyFilter, dateRange]);


  // Automation screen recording dialog (Watch Recording action in the table)
  const [recordingPolicy, setRecordingPolicy] = React.useState(null);

  // Re-submit confirmation. Holds the FRESH policy the freshness check
  // returned, so the dialog describes the job as the server currently has it
  // rather than as the table happened to render it.
  const [retryTarget, setRetryTarget] = React.useState(null);
  const [retrySubmitting, setRetrySubmitting] = React.useState(false);

  // Payment Dialog State (shown at the FINAL step, on Create/Update click)
  const [openPaymentDialog, setOpenPaymentDialog] = React.useState(false);
  const [paymentMode, setPaymentMode] = React.useState("wallet");
  const [pendingSubmitValues, setPendingSubmitValues] = React.useState(null);

  // The vehicle to price with: an edited policy whose vehicle is unchanged is
  // priced with the engine CC it was sold with, not the master's current one.
  const pricingVehicle = (data) =>
    vehicleForPricing(selectedMakeModel, savedSnapshot, data?.makeModel);

  // Multiple company: the TP insurer's own vehicle, which prices Total B (its CC's
  // TP rate, its TPPD rule). `selectedCombo` is the record picked — or, for a saved
  // policy, rebuilt from the policy so it is priced on the TP vehicle it was SOLD
  // with. Null in Single mode and for an old multi-company policy: one vehicle
  // prices both sides, exactly as before.
  const pricingTpVehicle = (data) =>
    data?.companyChoice === "multiple" ? tpVehicleOf({ mode: "multiple", combo: selectedCombo }) : null;

  // Handle next step with premium calculation (no auto-advance on step 0)
  const handleNextStep = async (data) => {
    setPolicyData(data);

    // Calculate premium on step 0 and stay on step 0
    if (activeStep === 0) {
      await calculatePremium(
        data,
        pricingVehicle(data),
        tpData,
        setOpenLoader,
        setCalculationResult,
        setShowCalculationResult,
        pricingTpVehicle(data)
      );
      return; // do not advance on calculate
    }

    handleNext();
  };

  // Step navigation: the payment method is now chosen at the FINAL step, so
  // Next simply advances (policyData stays synced with the form on step 0)
  const handleStepNext = () => {
    if (activeStep === 0 && formRef.current) {
      setPolicyData(formRef.current.values);
    }
    handleNext();
  };

  // Final submit (Create/Update on Preview): ask for the payment method first —
  // the policy is only created after Proceed in the dialog
  const handleFinalSubmit = (customerValues) => {
    setPendingSubmitValues(customerValues);
    setPaymentMode("wallet");
    fetchWalletChecks(
      { ...(policyData || {}), ...(formRef.current?.values || {}) },
      "wallet"
    );
    setOpenPaymentDialog(true);
  };

  const handlePaymentModeChange = (mode) => {
    setPaymentMode(mode);
    setWalletChecks([]);
    fetchWalletChecks(
      { ...(policyData || {}), ...(formRef.current?.values || {}) },
      mode
    );
  };

  const handlePaymentSubmit = async () => {
    if (sendingRef.current) return; // a double click on Proceed sends once
    sendingRef.current = true;
    setCreating(true);
    try {
      await submitWithPayment();
    } finally {
      sendingRef.current = false;
      setCreating(false);
    }
  };

  const submitWithPayment = async () => {
    // Merge the chosen payment method into policy data. Passed explicitly to
    // the save call too — React state updates are async and would be stale.
    const updatedPolicyData = {
      ...(policyData || {}),
      ...(formRef.current?.values || {}),
      Paymentmethod: paymentMode,
      paymentMode: paymentMode,
    };
    setPolicyData(updatedPolicyData);

    // Link → motor premium is paid directly by the customer; Brisk PA is still
    // checked and charged to the Brisk Company Wallet. Wallet → motor premium
    // and PA Cover (when on) are checked against their respective wallets.
    // Re-fetch because the balance shown when the dialog opened may be stale.
    // With the hold switch on, a Create goes to the server even when short: it answers with exactly what is short and
    // the page offers Cancel / Hold until money is added (saveCustomerDetails -> onInsufficientWallet).
    if (
      paymentMode === "link" ||
      (paymentMode === "wallet" && !(holdEnabled && !isEditMode))
    ) {
      const results = await fetchWalletChecks(updatedPolicyData, paymentMode);
      const shortfalls = results.filter((r) => !r.sufficient);

      if (shortfalls.length > 0) {
        const message = shortfalls
          .map((r) => `${r.company || "the selected"} wallet: ${describeWalletCheck(r)}`)
          .join("; ");
        ToastError(`Insufficient wallet balance — ${message}`);
        return;
      }
    }

    setOpenPaymentDialog(false);
    const submitValues = pendingSubmitValues || formRef.current?.values;
    if (submitValues) {
      await saveCustomerDetails(submitValues, updatedPolicyData);
    }
  };

  // Handle TPPD Restrict checkbox change (recalculate when on Basic step)
  const handleTppdRestrictChange = async (isChecked, formValues) => {
    if (activeStep === 0) {
      const baseData = policyData || formValues || {};
      const updatedData = { ...baseData, tppdRestrict: isChecked };
      setPolicyData(updatedData);

      await calculatePremium(
        updatedData,
        pricingVehicle(updatedData),
        tpData,
        setOpenLoader,
        setCalculationResult,
        setShowCalculationResult,
        pricingTpVehicle(updatedData)
      );
    }
  };

  // Handle Zero Depreciation checkbox and percentage change (recalculate when on Basic step)
  const handleZeroDepreciationChange = async (isChecked, formValues) => {
    if (activeStep === 0) {
      // Use formValues if policyData doesn't exist yet, otherwise use policyData
      const baseData = policyData || formValues;
      const updatedData = {
        ...baseData,
        zeroDepreciation: isChecked,
        zeroDepreciationPercentage: isChecked ? (formValues?.zeroDepreciationPercentage || 0.615) : 0,
      };
      setPolicyData(updatedData);

      await calculatePremium(
        updatedData,
        pricingVehicle(updatedData),
        tpData,
        setOpenLoader,
        setCalculationResult,
        setShowCalculationResult,
        pricingTpVehicle(updatedData)
      );
    }
  };

  const saveCustomerDetails = async (customerData, policyDataOverride = null, options = {}) => {
    // The server decides what is short on a Create when the hold switch is on (it offers Hold); the page's own
    // courtesy checks below stay for edits and for the switch off.
    const serverDecidesShortfall = holdEnabled && !isEditMode;
    // policyDataOverride carries the freshly-chosen payment method from the
    // dialog — React state (policyData) may not have updated yet
    const effPolicyData = policyDataOverride || policyData;

    // 1. Resolve Final Premium using the SAME logic as useOnlinePolicyForm hook
    console.log("Premium Resolution Debug:", {
      calculationResult,
      policyData: effPolicyData,
      editPolicyData,
      customerData,
      isEditMode
    });

    // Use the exact same resolution logic as in useOnlinePolicyForm.js (lines 161-171)
    // BUT ALSO check customerData fields since the premium might be stored there
    const resolvedFinalPremium = (() => {
      // First try the standard fields
      let rawValue =
        calculationResult && calculationResult.finalPremium != null
          ? calculationResult.finalPremium
          : effPolicyData?.finalPremium != null
            ? effPolicyData.finalPremium
            : editPolicyData?.finalPremium;

      // If still not found, check customerData
      if (rawValue == null || (typeof rawValue === 'number' && isNaN(rawValue))) {
        // The PreviewStep shows: calculationResult.finalPremium + customerValues.paCoverAmount
        // So if calculationResult.finalPremium is NaN, the total might be in paCoverAmount
        rawValue = customerData?.netPremium || customerData?.finalPremium;
      }

      const numericValue = Number(rawValue);
      return Number.isFinite(numericValue) ? numericValue : 0;
    })();

    let finalPremium = resolvedFinalPremium;

    console.log("Resolved Premium:", {
      rawValue: calculationResult?.finalPremium || policyData?.finalPremium || editPolicyData?.finalPremium || customerData?.netPremium,
      numericValue: Number(calculationResult?.finalPremium || policyData?.finalPremium || editPolicyData?.finalPremium || customerData?.netPremium),
      finalPremium,
      customerDataNetPremium: customerData?.netPremium,
      customerDataPaCoverAmount: customerData?.paCoverAmount
    });

    // 1b. Policy Wallet gate — a USER login may only create a Motor Policy
    // if their admin-approved Policy Wallet balance covers the calculated
    // premium. A separate, user-level wallet from the Company Wallet check
    // below — but, like it, only when the policy is paid from the wallet: with
    // "Send Payment Link" the customer pays directly and the server debits
    // nothing (onlinePolicyDao: `paymentMethod !== "link"`), so this check must
    // not refuse it either. Same payment-method resolution as the server.
    const currentPaymentMode = effPolicyData?.paymentMode || paymentMode;
    const isLinkPayment =
      String(
        effPolicyData?.Paymentmethod || effPolicyData?.paymentMode || paymentMode || "wallet"
      ).toLowerCase() === "link";
    const UserType = sessionStorage.getItem("userType");
    // Not on an edit: the server refunds the old charge first and refuses with the real figures, whereas this
    // courtesy check would compare the whole premium with the balance and block an edit that would succeed.
    if (checkUserType(UserType).isUser && !isEditMode && !isLinkPayment && !serverDecidesShortfall) {
      const walletRes = await GetPolicyWalletBalance();
      const approvedBalance = Number(walletRes?.balance) || 0;
      if (finalPremium > approvedBalance) {
        ToastError(
          `Insufficient Policy Wallet balance — this policy needs ₹${finalPremium}, your approved balance is ₹${approvedBalance}`
        );
        return;
      }
    }

    // 2. Wallet check — link means the customer pays directly, nothing to
    // check. Wallet means motor premium is always checked, PA Cover (when
    // on) is an additional check against Brisk or Company. Re-fetch right
    // here rather than trusting the balance shown earlier in the dialog:
    // this is the last moment before the actual create call, and the
    // balance can have changed since (another topup/remove, or another
    // policy debiting the same wallet) — the backend re-checks this exact
    // same thing atomically at creation time regardless, so this is a
    // courtesy that avoids a round trip for an obviously-doomed submit, not
    // the authoritative check.
    if (currentPaymentMode === "wallet" && !serverDecidesShortfall) {
      const results = await fetchWalletChecks(effPolicyData);
      const shortfalls = results.filter((r) => !r.sufficient);

      if (shortfalls.length > 0) {
        const message = shortfalls
          .map((r) => `${r.company || "the selected"} wallet: ${describeWalletCheck(r)}`)
          .join("; ");
        ToastError(`Insufficient wallet balance — ${message}`);
        return;
      }
    }

    // If premium is 0 but calculation was shown, there's a data issue
    // Allow creation to proceed and let backend handle it, but warn the user
    if (finalPremium <= 0) {
      if (showCalculationResult) {
        console.warn("⚠️ Premium is 0 but calculation was shown. Proceeding with policy creation - backend will calculate premium.");
        // Continue to policy creation - don't return
      } else {
        ToastError("Invalid premium amount. Please recalculate.");
        return;
      }
    }

    const result = await saveCustomerDetailsHook(
      customerData,
      effPolicyData,
      isEditMode,
      editPolicyData,
      policyId,
      setOpenLoader,
      {
        createRequestId: isEditMode ? undefined : createTracker.current.idFor({ create: true, hold: !!options.holdForPayment }),
        holdForPayment: !!options.holdForPayment,
        onInsufficientWallet: holdEnabled
          ? (res) => setHoldOffer({ message: res.message, customerData, policyData: effPolicyData })
          : undefined,
      }
    );
    // A clear answer burns the id (the next Create is a new one); only "no answer" may send the same id again.
    if (!(result && (result.noResponse || result.code === "IN_PROGRESS"))) createTracker.current.reset();
  };

  // "Hold until money is added" from the short-wallet question: the same Create again, saved on hold.
  const createOnHold = async () => {
    const offer = holdOffer;
    setHoldOffer(null);
    if (!offer || sendingRef.current) return;
    sendingRef.current = true;
    setCreating(true);
    try {
      await saveCustomerDetails(offer.customerData, offer.policyData, { holdForPayment: true });
    } finally {
      sendingRef.current = false;
      setCreating(false);
    }
  };

  // Handle Edit Policy
  // Job states in which a policy must NOT be edited: it is either being
  // submitted or already submitted, so an edit would re-send a policy the
  // insurer may have accepted.
  const NON_EDITABLE_JOB_STATUSES = [
    "processing",
    "completed",
    "Complete",
    "completed_with_errors",
  ];

  const COMPLETED_JOB_STATUSES = [
    "completed",
    "Complete",
    "completed_with_errors",
  ];

  const FAILED_JOB_STATUSES = [
    "failed",
    "failed_post_submission",
    "failed_login_form",
    "failed_validation",
    "failed_duplicate",
    "error",
  ];

  /**
   * Is this policy being paid by a link sent to the customer?
   *
   * It matters for every rule below. A wallet policy is paid the moment it is
   * created, so once its automation job completes the policy is finished and
   * touching it again would re-submit something the insurer already accepted.
   * A payment-link policy is NOT paid at completion — the customer still has
   * the link in hand — so the operator has to be able to correct it and send
   * it through again right up until they pay.
   */
  const isPaymentLink = (policy) =>
    String(policy?.Paymentmethod || policy?.paymentMode || "").toLowerCase() ===
    "link";

  /**
   * Is the job locked against edit / re-submit?
   *
   * "processing" — the job, or any one of its parts — is locked for everyone:
   * the automation is holding it this second and a second run would collide
   * with a live browser session. The completed states are locked only for
   * wallet policies (see above): once every motor part (OD + TP) is bought the
   * policy is finished. A policy with only SOME parts bought (TP done, OD
   * failed) is not — its unfinished parts can still be retried and edited.
   */
  const jobIsLocked = (policy) => {
    const status = policy?.jobStatus || "pending";
    if (status === "processing") return true;
    if (realParts(policy).some((p) => p.status === "processing")) return true;
    if (allMotorPartsDone(policy)) return !isPaymentLink(policy);
    if (COMPLETED_JOB_STATUSES.includes(status)) return !isPaymentLink(policy);
    return false;
  };

  /**
   * Is EDITING locked? Only while a part is running. A COMPLETED wallet policy can now be edited too (when it has
   * parts): the editor may tick a bought part to buy it again, which charges the full new amount once more. Retry
   * keeps using jobIsLocked, because nothing is "retried" on a finished policy.
   */
  const editIsLocked = (policy) => {
    const status = policy?.jobStatus || "pending";
    if (status === "processing") return true;
    if (realParts(policy).some((p) => p.status === "processing")) return true;
    const finished = allMotorPartsDone(policy) || COMPLETED_JOB_STATUSES.includes(status);
    if (finished && !isPaymentLink(policy)) return !hasRealParts(policy);
    return false;
  };

  /** Should the row show a Re-submit button? */
  const canRetryRow = (row) => {
    const status = row?.jobStatus || "pending";
    if (status === "pending" || status === "processing") return false;
    // Every motor part bought: nothing left to retry, unless the customer has
    // not paid the link yet.
    if (allMotorPartsDone(row)) return isPaymentLink(row);
    // Finished, but the customer has not paid the link yet — let it go again.
    if (COMPLETED_JOB_STATUSES.includes(status) && isPaymentLink(row))
      return true;
    if (status === "completed" || status === "Complete") return false;
    return (
      FAILED_JOB_STATUSES.includes(status) || row?.attempts >= row?.maxAttempts
    );
  };

  /**
   * Held: parked by an operator so the automation leaves it alone.
   *
   * The automation only ever claims jobs whose status is exactly "pending", so
   * a job sitting on "hold" is simply never picked up.
   */
  const isHeld = (policy) => (policy?.jobStatus || "") === "hold";

  /**
   * Hold is offered on a PENDING job only — that is the one window where the
   * work has not started and can still be stopped. Once the automation has
   * claimed it there is nothing to hold, and a finished or failed job has
   * nothing left to stop.
   */
  const canHoldRow = (row) => {
    const status = row?.jobStatus || "pending";
    // While it RUNS, Hold is a request: the automation stops at its next safe step and holds it (lib/holdStop.js).
    return status === "pending" || (status === "processing" && !row?.holdRequested);
  };

  /** Should the row show an Edit button? */
  const canEditRow = (row) => {
    const status = row?.jobStatus;
    if (allMotorPartsDone(row) || COMPLETED_JOB_STATUSES.includes(status)) {
      return isPaymentLink(row) || hasRealParts(row);
    }
    return (
      status === undefined ||
      status === "failed" ||
      // A held job is the one you most want to edit — holding it is how you
      // buy time to correct it. The hold survives the edit (enqueuePolicyJob).
      status === "hold" ||
      status === "pending" ||
      status === "failed_post_submission" ||
      status === "failed_login_form" ||
      status === "failed_validation"
    );
  };

  /** Anything worth opening the error logs for. */
  const hasErrorLogs = (row) =>
    [
      "failed",
      "pending",
      "failed_post_submission",
      "failed_login_form",
      "failed_validation",
      "failed_duplicate",
      "completed_with_errors",
    ].includes(row?.jobStatus) || realParts(row).some((p) => p.lastError);

  // Raw job states are internal names. Show wording a user recognises.
  const JOB_STATUS_LABELS = {
    pending: "waiting to start",
    hold: "on hold",
    processing: "in progress",
    completed: "completed",
    Complete: "completed",
    completed_with_errors: "completed with some errors",
    failed: "failed",
    failed_post_submission: "failed",
    failed_login_form: "failed",
    error: "failed",
  };

  const statusLabel = (status) =>
    JOB_STATUS_LABELS[status] || String(status || "waiting to start");

  /**
   * Re-check a row against the server before acting on it.
   *
   * The table renders once; the automation server keeps working. A row can
   * therefore be minutes stale — its Edit button may still be showing for a job
   * that has since started or finished. Without this, a user on an old page
   * could edit a policy the insurer has already accepted.
   *
   * Silently refetches, tells the user when the status moved on, refreshes the
   * table so the buttons match reality, and blocks the action when the new
   * status no longer allows it.
   *
   * @returns {object|null} the fresh policy, or null when the action must stop
   */
  const getCurrentPolicyForAction = async (row, { forEdit = false } = {}) => {
    const policyId = row?._id || row?.policyId || row?.id;
    if (!policyId) return null;

    const fresh = await fetchPolicyByIdSilent(policyId);

    // Could not check — let the action continue rather than blocking work on a
    // transient network blip. The action's own error handling still applies.
    if (!fresh) return { _id: policyId };

    const wasStatus = row?.jobStatus || "pending";
    const nowStatus = fresh?.jobStatus || "pending";
    // One part can move (OD failed -> pending) while the job's overall status
    // stays put, so the parts' own statuses count as "moved on" too.
    const movedOn =
      String(wasStatus) !== String(nowStatus) ||
      partsSignature(row) !== partsSignature(fresh);
    const nowText =
      hasRealParts(fresh) && fresh.statusLabel
        ? fresh.statusLabel
        : statusLabel(nowStatus);

    if (movedOn) {
      // Bring the row in line so its buttons stop lying — just this row. A full
      // reload here dropped the whole table behind a loader to change one chip,
      // which read as the page refreshing under the user mid-click.
      patchTableRow(policyId, {
        jobStatus: fresh.jobStatus,
        attempts: fresh.attempts ?? row?.attempts ?? 0,
        maxAttempts: fresh.maxAttempts ?? row?.maxAttempts ?? 3,
        parts: fresh.parts || [],
        statusLabel: fresh.statusLabel ?? "",
        lastError: fresh.lastError ?? null,
      });
    }

    if (forEdit && editIsLocked(fresh)) {
      ToastError(
        `This policy is ${statusLabel(nowStatus)} and can no longer be edited. The list has been updated.`
      );
      return null;
    }

    if (movedOn) {
      ToastSuccess(`This policy is now ${nowText}. Showing the latest details.`);
    }

    return fresh;
  };

  const handleEditPolicy = async (policyId, rowParts) => {
    const fetched = await fetchPolicyById(policyId);
    if (!fetched) return;
    // The parts drive the notice and the Re-submit panel. The policy fetch
    // carries them; the table row's copy stands in when it does not.
    const policy = fetched.parts?.length
      ? fetched
      : { ...fetched, parts: rowParts || [] };

    console.log("Raw dates from API:", {
      purchaseDate: policy.purchaseDate,
      registrationDate: policy.registrationDate,
      dateOfBirth: policy.dateOfBirth,
      nomineeDob: policy.nomineeDob,
    });

    // Set edit mode and policy data
    setIsEditMode(true);
    setEditPolicyData(policy);
    // Keep what the policy was sold with (see utils/masterDrift): the saved
    // premium stands in for a calculation, so an untouched edit shows "Next"
    // rather than "Calculate".
    setSavedSnapshot(savedSnapshotFromPolicy(policy));
    const savedCalculation = calculationFromSavedPolicy(policy);
    setCalculationResult(savedCalculation);
    setShowCalculationResult(!!savedCalculation);
    setShowForm(true);

    // Find the selected vehicle from vehicleData
    const selectedVehicle = vehicleData.find(
      (v) =>
        v.VehicleMake === policy.make ||
        v.ID_PK === policy.makeModel ||
        v.VehicleModel === policy.vehicleModel
    );

    if (selectedVehicle) {
      setSelectedMakeModel(selectedVehicle);
    }
    // A policy sold from a Combined Make & Model opens in Multiple with its record,
    // rebuilt from the policy itself (name, companies, the TP vehicle it was sold
    // with). Null for a single-company policy and for an old multi-company one.
    setSelectedCombo(comboFromPolicy(policy));

    // Pre-populate the form with existing data
    setTimeout(() => {
      if (formRef.current && formRef.current.setValues) {
        const formValues = {
          // Company + Product Selection + `settings` — setValues replaces the
          // whole object, so anything missing here is wiped (the PA Cover
          // Company dropdown once fell back to the single `company` because
          // OD/TP were dropped). routingFromPolicy also supplies `company`,
          // paCoverCompany and paCoverInsurerCompany, consistent with settings.
          ...routingFromPolicy(policy),
          // Single / Multiple, and the Combined Make & Model it was sold from.
          ...selectionFromPolicy(policy),

          // Basic Policy Details
          makeModel: policy.makeModel || selectedVehicle?.ID_PK || "",
          rtoCityLocation: normalizeRtoCode(policy.rtoCityLocation),
          RTORegion: policy.RTORegion || "",
          RTOCity: policy.RTOCity || "",
          // Both were dropped here, so the zone and variant the policy was
          // priced with came back from the master instead.
          zone: policy.zone || "",
          vehicleVariant: policy.vehicleVariant || "",
          idv: policy.idv || "",
          // setValues REPLACES the whole values object, so anything missing
          // from this map is wiped. These four were — leaving the IDV field
          // enabled with no selling price until the Make & Model sync happened
          // to run again, which it only did when it lost the race with this
          // very setValues. BasicPolicyDetailsStep re-asserts them from the
          // master; restoring them here is what stops the flicker.
          modelNotFound: policy.modelNotFound || false,
          manufacturerSellingPrice: policy.manufacturerSellingPrice || "",
          idvRangeFrom: policy.idvRangeFrom || "",
          idvRangeTo: policy.idvRangeTo || "",
          // range / price set by hand in the IDV Calculator (this policy only)
          idvByHand: !!policy.idvByHand,
          discount: policy.discount !== undefined && policy.discount !== null ? String(policy.discount) : "",
          manufacturingYear: policy.manufacturingYear || "",
          manufacturingMonth: policy.manufacturingMonth || "",
          purchaseDate: parseMongoDate(policy.purchaseDate),
          registrationDate: parseMongoDate(policy.registrationDate),
          zeroDepreciation: policy.zeroDepreciation || false,
          zeroDepreciationPercentage:
            policy.zeroDepreciation
              ? (typeof policy.zeroDepreciationPercentage !== "undefined" && policy.zeroDepreciationPercentage !== null
                ? policy.zeroDepreciationPercentage
                : 0.615)
              : 0,
          tppdRestrict: policy.tppdRestrict || false,
          paCover: policy.paCover || false,
          paCoverAmount: policy.paCoverAmount || "",
          notPaCoverReason: policy.notPaCoverReason || "",
          nomineeName: policy.nomineeName || "",
          nomineeGender: policy.nomineeGender || "",
          nomineeRelation: policy.nomineeRelation || "",
          nomineeDob: parseMongoDate(policy.nomineeDob),
          nomineeAge: policy.nomineeAge || "",
          paCoverYears: policy.paCoverYears || 1,
          // A saved policy's PA term counts as chosen (a National TP's picker would otherwise open empty).
          _paYearsChosen: true,
          otherRelationName: policy.otherRelationName || "",
          appointeeName: policy.appointeeName || "",
          // KSHEMA only. Same defaults as a new policy rather than "", so a
          // policy saved before these existed opens on valid values instead of
          // an empty dropdown and a share that fails validation.
          nomineeType: policy.nomineeType || "individual",
          nomineeShare: policy.nomineeShare ?? "100",

          // Financier — these were NOT restored at all, so on edit the section
          // came up empty and an update wrote the saved financier back as "".
          hasFinancier: policy.hasFinancier || false,
          financierType: policy.financierType || "",
          financierName: policy.financierName || "",
          financierAddress: policy.financierAddress || "",
          // The customer's existing TP policy (OD only / two insurers) — CustomerDetailsStep "Third party (TP) policy"
          existingTpPolicyNumber: policy.existingTpPolicyNumber || "",
          existingTpInsurer: policy.existingTpInsurer || "",
          existingTpStartDate: parseMongoDate(policy.existingTpStartDate),
          existingTpEndDate: parseMongoDate(policy.existingTpEndDate),

          // Customer Details
          fullName: policy.fullName || policy.firstName || "",
          surname: policy.surname || policy.lastName || "",
          kshemaSendSurname: policy.kshemaSendSurname === true,
          dateOfBirth: parseMongoDate(policy.dateOfBirth),
          gender: policy.gender || "",
          // A saved title is the operator's choice; an older policy without one starts from its Gender on the next save.
          proposerTitle: policy.proposerTitle || titleFromGender(policy.gender) || "",
          _titleChosen: !!policy.proposerTitle,
          fatherName: policy.fatherName || "",
          // OCR-filled on create and never re-run on edit, so without carrying
          // it over the Aadhaar number bar showed "not clear" on a policy whose
          // number had been read perfectly well the first time.
          aadharNumber: policy.aadharNumber || "",

          // Address Details
          flatDoorNo: policy.flatDoorNo || "",
          floorNo: policy.floorNo || "",
          buildingName: policy.buildingName || "",
          blockName: policy.blockName || "",
          roadStreetLane: policy.roadStreetLane || "",
          areaAndLocality: policy.areaAndLocality || "",
          state: policy.state || "tamilnadu",
          country: policy.country || "India",
          pincode: policy.pincode || "",
          email: policy.email || "",
          mobileNumber: policy.mobileNumber || "",

          // Vehicle Details
          engineNumber: policy.engineNumber || "",
          chassisNumber: policy.chassisNumber || "",

          // Already-uploaded documents. These are stored objects
          // ({ fileName, key, location, presignedUrl }) — NOT File objects.
          // Without carrying them over, the edit form looked as if nothing had
          // ever been uploaded and the user could not see the existing
          // Aadhaar / PAN card.
          aadharCard: policy.aadharCard || null,
          panCard: policy.panCard || null,
        };

        formRef.current.setValues(formValues);
        console.log("Form populated with values for edit:", formValues);
        console.log("Discount value set to:", formValues.discount);
      }
    }, 100);

    ToastSuccess("Policy data loaded for editing");
  };

  /**
   * Which policy field each column sorts on.
   *
   * The columns are named after the pre-rendered element they hold
   * ("renderCreatedAt"), and MasterCommonTable sends that name back as the sort
   * key — so it has to be translated to something the server recognises. A
   * column missing from this map is simply not sortable; Status is the notable
   * one, since jobStatus is joined in after the sort stage runs.
   */
  const SORT_FIELD_BY_COLUMN = {
    renderCreatedAt: "createdAt",
    renderCompany: "company",
    renderPolicyRef: "policyId",
    renderCustomerName: "fullName",
    renderMakeModel: "vehicleMake",
    renderIdv: "idv",
    renderPremium: "finalPremium",
  };

  /**
   * Table header click -> server sort.
   *
   * MasterCommonTable emits `sort` as "field" or "-field" for descending.
   */
  const handleTableSort = (sortToken) => {
    const raw = String(sortToken || "");
    const descending = raw.startsWith("-");
    const column = descending ? raw.slice(1) : raw;
    const field = SORT_FIELD_BY_COLUMN[column];

    if (!field) return false; // not a sortable column — leave the order alone

    setSort({ sortBy: field, sortOrder: descending ? "desc" : "asc" });
    return true;
  };

  // Company is stored as a plain string on the policy, so the filter's value IS
  // the company name. FilterBox matches its options on `_id`.
  const companyFilterOptions = useMemo(
    () =>
      SUPPORTED_COMPANIES.map((name) => ({
        _id: name,
        label: name,
        value: name,
      })),
    []
  );

  // Bound to the DRAFT values — see the note on draftCompany above.
  const filterBoxData = [
    {
      id: 1,
      label: "Select Company",
      data: companyFilterOptions,
      setState: setDraftCompany,
      value: draftCompany,
      type: "input",
      variation: "single",
    },
    {
      id: 2,
      // Created-date range. "noDefaultDate" is FilterBox's plain range picker;
      // the default (this month) is supplied here rather than by the picker.
      type: "noDefaultDate",
      label: "Created Date",
      setDate: (range) =>
        setDraftDateRange(range ? [range[0], range[1]] : [null, null]),
      defaultValue:
        draftDateRange?.[0] && draftDateRange?.[1] ? draftDateRange : null,
      value: draftDateRange?.[0] && draftDateRange?.[1] ? draftDateRange : null,
    },
  ];

  /** Clear All — back to the page defaults (this month, every company). */
  const handleClearAllState = () => {
    resetFilters();
    setFilterBoxKey((k) => k + 1); // wipe FilterBox's own selection state
  };

  /**
   * Search — commit the draft and query once.
   *
   * Fired by the Search button only, NOT by FilterBox. FilterBox calls its
   * handleSaveAllFilter prop on every select/deselect in BOTH modes — autoApply
   * only decides which of its code paths does it, not whether it happens — so
   * that prop is deliberately a no-op below and this is wired to a button of
   * our own instead.
   *
   * The load is fired here rather than left to an effect on the applied values:
   * pressing Search without having changed anything must still re-query, and
   * unchanged state would not re-trigger an effect.
   */
  const applyFilters = () => {
    const company = draftCompanyRef.current;
    const range = draftDateRangeRef.current;
    const nextRange = range?.[0] && range?.[1] ? range : [null, null];

    setCompanyFilter(company);
    setDateRange(nextRange);
    setPage(0);

    loadAllOnlinePolicies(
      debouncedSearchQuery ? { search: debouncedSearchQuery } : null,
      0,
      rowsPerPage,
      { company, dateRange: nextRange, sort }
    );
  };

  /**
   * What FilterBox gets as handleSaveAllFilter: nothing.
   *
   * It fires this on every dropdown change — which is exactly what must not
   * happen here. Applying is the Search button's job.
   */
  const handleSaveAllFilter = () => { };

  /** The per-filter Clear inside the panel. Edits the draft, like the controls
   *  themselves — Search is still what applies it. */
  const handleClearSingleFilter = (filterId) => {
    if (filterId === 1) setDraftCompany(null);
    if (filterId === 2) setDraftDateRange([null, null]);
  };

  // Counts what is APPLIED, not what is drafted — the badge on the closed panel
  // has to describe the list on screen.
  const activeFiltersCount = [
    companyFilter,
    dateRange?.[0] && dateRange?.[1] ? "dateRange" : null,
  ].filter(Boolean).length;

  // Every column is a pre-rendered `render*` element built in
  // processedTableData below and handed over by cellColumn, which keeps the
  // table's fixed-height text wrapper out of the way.
  const columns = [
    cellColumn("renderCreatedAt", "Date"),
    cellColumn("renderCompany", "Company"),
    cellColumn("renderPolicyRef", "Policy / Proposal"),
    cellColumn("renderCustomerName", "Customer Name"),
    cellColumn("renderMakeModel", "Make & Model"),
    cellColumn("renderIdv", "IDV"),
    cellColumn("renderPremium", "Premium"),
    cellColumn("renderJobStatus", "Status"),
    cellColumn("renderActions", "Actions"),
  ];

  // Pre-process table data to include custom rendering for Chips and Actions
  const processedTableData = useMemo(() => {
    return tableData.map((row) => {
      const created = formatCreatedAt(row.createdAt);
      // Written by the automation once the portal returns one; until then the
      // policy only has the OP… id our own save generated.
      const premium =
        row.finalPremium ?? row.premiumCalculation?.finalPremium ?? null;

      return {
        ...row,
        // Creation stamp, date over time so neither has to wrap.
        renderCreatedAt: (
          <TableTextCell
            primary={created.date}
            secondary={created.time}
            maxWidth={110}
          />
        ),
        // A multi-company policy shows both insurers: OD on the first line,
        // TP underneath. The backend fills `settings` for older rows too.
        renderCompany: row.settings?.isMultipleCompany ? (
          <TableTextCell
            primary={`${String(row.settings.ODCompany).toUpperCase()} OD`}
            secondary={`${String(row.settings.TPCompany).toUpperCase()} TP`}
            maxWidth={110}
          />
        ) : (
          <TableTextCell
            primary={row.company ? String(row.company).toUpperCase() : "-"}
            maxWidth={110}
          />
        ),
        // Policy / Proposal reference, most authoritative first:
        //   policyNumber   — the issued policy. Only exists once the insurer
        //                    has actually issued it.
        //   proposalNumber — the referral number the portal returns the moment
        //                    the proposal is saved (Reliance: R200826103561).
        //                    This is the only reference that exists in the gap
        //                    between submitting and issuing.
        //   policyId       — the OP… id we generate ourselves on save.
        // Hovering spells out which is which, since one bare number tells you
        // nothing about what kind of number it is.
        renderPolicyRef: (
          <TableTextCell
            primary={row.policyNumber || row.proposalNumber || row.policyId || "-"}
            maxWidth={150}
            mono
            title={<PolicyNumbersTip row={row} />}
          />
        ),
        // firstName is the older field name — policies saved before the rename
        // still carry only that one. Surname is the caption rather than part of
        // the same line so a long full name still has the whole width.
        //
        // Hovering gives the contact details. They are the reason anyone looks
        // a customer up from this list — to call or mail them about a stuck
        // submission — and putting them in columns of their own would cost the
        // width the policy details need.
        renderCustomerName: (
          <TableTextCell
            primary={row.fullName || row.firstName || "-"}
            secondary={row.surname || ""}
            maxWidth={170}
            title={
              <Box sx={{ py: 0.25 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  {[row.fullName || row.firstName, row.surname]
                    .filter(Boolean)
                    .join(" ") || "-"}
                </Typography>
                <Typography sx={{ fontSize: 12, mt: 0.25 }}>
                  Mobile: <strong>{row.mobileNumber || "not recorded"}</strong>
                </Typography>
                <Typography sx={{ fontSize: 12 }}>
                  Email: <strong>{row.email || "not recorded"}</strong>
                </Typography>
              </Box>
            }
          />
        ),
        // The CC used to have a column of its own. It identifies the vehicle
        // rather than standing on its own, so it rides along as the caption.
        renderMakeModel: (
          <TableTextCell
            primary={
              [row.vehicleMake, row.vehicleModel].filter(Boolean).join(" ") ||
              "-"
            }
            // A policy sold from a Combined Make & Model has a second vehicle (the
            // TP insurer's); it rides along under the first.
            secondary={[
              row.vehicleCC ? `${row.vehicleCC} CC` : "",
              row.tpVehicle
                ? `TP: ${[row.tpVehicle.vehicleMake, row.tpVehicle.vehicleModel, row.tpVehicle.vehicleVariant]
                    .filter(Boolean)
                    .join(" ")}`
                : "",
            ]
              .filter(Boolean)
              .join(" · ")}
            maxWidth={190}
          />
        ),
        // A model-not-found policy stores idv 0 on purpose — the insurer's
        // portal derives it from the selling price. Printing ₹0 there would
        // read as a free vehicle rather than as "not ours to set".
        renderIdv: (
          <TableTextCell
            primary={
              Number(row.idv) > 0
                ? formatCurrency(row.idv, { decimals: 0 })
                : row.modelNotFound
                  ? "Portal derived"
                  : "-"
            }
            maxWidth={120}
          />
        ),
        renderPremium: (
          <TableTextCell primary={formatCurrency(premium)} maxWidth={130} />
        ),
        // Render Job Status Chip with Tooltip
        renderJobStatus: (() => {
          const rawStatus = row.jobStatus || "pending";
          // The list keeps every failure flavour under one "Failed" label —
          // "failed_login_form" read as raw enum text on the table, and the
          // distinction it draws only matters on the detail page (see
          // jobStatusLabel's own comment on this). The real reason goes on
          // hover instead of being dropped.
          const isFailedStatus =
            rawStatus === "failed" ||
            rawStatus === "failed_post_submission" ||
            rawStatus === "failed_login_form" ||
            rawStatus === "failed_validation" ||
            rawStatus === "failed_duplicate" ||
            rawStatus === "error";
          const chipLabel = isFailedStatus ? "Failed" : rawStatus;

          // A completed payment-link policy is NOT finished — the customer
          // still has the link to pay — which is why it keeps its Edit and
          // Re-submit buttons while a completed wallet policy does not.
          const captions = (
            <>
              {isPaymentLink(row) &&
                COMPLETED_JOB_STATUSES.includes(row?.jobStatus) && (
                  <Typography
                    sx={{ fontSize: 10.5, fontWeight: 700, color: "#B54708" }}
                  >
                    payment link · can re-submit
                  </Typography>
                )}
              {isHeld(row) && (
                <Typography
                  sx={{ fontSize: 10.5, fontWeight: 700, color: isWaitingForMoney(row) ? "#B42318" : "#B54708" }}
                >
                  {isWaitingForMoney(row) ? "waiting for wallet money" : "automation paused"}
                </Typography>
              )}
              {!isHeld(row) && row?.holdRequested && (
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#B54708" }}>
                  hold requested · stopping at the next safe step
                </Typography>
              )}
            </>
          );

          // OD / TP / PA each have a status of their own: one line per part
          // rather than one chip (a chip plus three lines is too tall).
          if (hasRealParts(row)) {
            // The numbers are in the Policy / Proposal column (once); this column is about status.
            return (
              <PartStatusList parts={realParts(row)} hideNumbers>
                {captions}
              </PartStatusList>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.4,
              }}
            >
              <Tooltip
                title={
                  isFailedStatus
                    ? row.lastError
                      ? friendlyErrorLine(row.lastError, { stage: row.stage, errorType: row.jobStatus === "failed_post_submission" ? "PostSubmissionError" : undefined })
                      : "Failed — no further details available."
                    : `Attempts: ${row.attempts || 0} / ${row.maxAttempts || 3}`
                }
              >
                <Chip
                  label={chipLabel}
                  color={getStatusColor(row.jobStatus)}
                  size="small"
                />
              </Tooltip>
              {captions}
            </Box>
          );
        })(),
        // Render Actions Buttons
        renderActions: (
          <Box sx={{ display: "flex", gap: 0.75 }}>
            {/* Hold — only while the job is still queued. */}
            {canHoldRow(row) && (
              <AsyncIconButton
                title={
                  (row?.jobStatus || "pending") === "processing"
                    ? "Hold — stop the running automation at its next safe step and hold it"
                    : "Hold — stop the automation picking this up"
                }
                color={NAVY}
                sx={ACTION_SX}
                onClick={async () => {
                  // Same freshness re-check the Edit button does: this row may
                  // have been rendered before the automation claimed the job,
                  // in which case there is nothing left to hold.
                  const fresh = await getCurrentPolicyForAction(row);
                  if (!fresh) return;
                  const status = fresh.jobStatus || "pending";
                  // Running: Hold asks first (StopRunDialog); the automation then stops at its next safe step.
                  if (status !== "pending" && !(status === "processing" && !fresh.holdRequested)) {
                    ToastError(
                      status === "processing"
                        ? "Too late to hold — the automation has already started this policy. The list has been updated."
                        : `This policy is ${statusLabel(status)} — there is nothing to hold. The list has been updated.`
                    );
                    return;
                  }
                  // Running: ask first, showing the step it is on (the automation stops at its next safe step).
                  if (status === "processing") {
                    setStopConfirm(fresh);
                    return;
                  }
                  await setPolicyHold(fresh._id || row._id, true);
                }}
              >
                <PauseCircleOutlineIcon fontSize="small" />
              </AsyncIconButton>
            )}
            {/* A hold asked for while it ran: withdraw it (the automation then carries on as normal). */}
            {!isHeld(row) && row?.holdRequested && (
              <AsyncIconButton
                title="Cancel the hold request — let the automation carry on"
                color={NAVY}
                sx={ACTION_SX}
                onClick={async () => {
                  await setPolicyHold(row._id, false);
                }}
              >
                <PlayCircleOutlineIcon fontSize="small" />
              </AsyncIconButton>
            )}
            {/* Release — put a held job back in the queue. */}
            {isHeld(row) && (
              <AsyncIconButton
                title={
                  isWaitingForMoney(row)
                    ? "Remove hold — charge the wallet and start the automation"
                    : "Release — put this back in the automation queue"
                }
                color={NAVY}
                sx={ACTION_SX}
                onClick={async () => {
                  const fresh = await getCurrentPolicyForAction(row);
                  if (!fresh) return;
                  // Waiting for wallet money: say what will be charged before anything moves.
                  if (isWaitingForMoney(fresh) || isWaitingForMoney(row)) {
                    setChargeConfirm(fresh.payment ? fresh : row);
                    return;
                  }
                  await setPolicyHold(fresh._id || row._id, false);
                }}
              >
                <PlayCircleOutlineIcon fontSize="small" />
              </AsyncIconButton>
            )}
            {/* Re-submit. Shown for a failed job, and — uniquely — for a
                COMPLETED payment-link policy, which the customer has not paid
                for yet and so is not finished. */}
            {canRetryRow(row) && (
              <AsyncIconButton
                title={
                  COMPLETED_JOB_STATUSES.includes(row?.jobStatus) ||
                  allMotorPartsDone(row)
                    ? "Submit Again (payment link not paid yet)"
                    : anyPartCompleted(row)
                      ? `Re-submit unfinished parts (${joinNames(
                          unfinishedParts(row),
                          { conjunction: "" }
                        )})`
                      : `Retry Policy (${row.attempts || 0}/${row.maxAttempts || 3})`
                }
                color={NAVY}
                sx={ACTION_SX}
                onClick={async () => {
                  const fresh = await getCurrentPolicyForAction(row);
                  if (!fresh) return;
                  // A job that finished while this page sat open needs no
                  // retry — unless it is a payment-link policy, where a
                  // completed job is still re-submittable.
                  if (jobIsLocked(fresh)) {
                    ToastError(
                      `This policy is ${statusLabel(
                        fresh.jobStatus || "pending"
                      )} — there is nothing to retry. The list has been updated.`
                    );
                    return;
                  }
                  // Ask first. This throws away the existing queue entry and
                  // sends the policy to the insurer again — not something to
                  // fire off a single stray click.
                  setRetryTarget({
                    id: fresh._id || row._id,
                    name: row.fullName || row.firstName || "this policy",
                    policyRef: row.policyNumber || row.policyId || "",
                    jobStatus: fresh.jobStatus || row.jobStatus || "pending",
                    attempts: fresh.attempts ?? row.attempts ?? 0,
                    maxAttempts: fresh.maxAttempts ?? row.maxAttempts ?? 3,
                    paidByLink: isPaymentLink(fresh) || isPaymentLink(row),
                    parts: fresh.parts?.length ? fresh.parts : row.parts || [],
                    statusLabel: fresh.statusLabel || row.statusLabel || "",
                  });
                }}
              >
                <ReplayIcon fontSize="small" />
              </AsyncIconButton>
            )}
            {hasErrorLogs(row) && (
                <AsyncIconButton
                  title="View issues"
                  color="#B42318"
                  sx={ISSUE_ACTION_SX}
                  onClick={async () => {
                    // Read-only: refresh so newer errors are included, but
                    // never block.
                    await getCurrentPolicyForAction(row);
                    await handleViewPolicyErrorLogs(row._id || row.policyId || row.id);
                  }}
                >
                  <ReportProblemOutlinedIcon fontSize="small" />
                </AsyncIconButton>
              )}
            {canEditRow(row) && (
              <AsyncIconButton
                  title={
                    COMPLETED_JOB_STATUSES.includes(row?.jobStatus)
                      ? "Edit Policy (payment link not paid yet)"
                      : "Edit Policy"
                  }
                  color={NAVY}
                  sx={ACTION_SX}
                  onClick={async (event) => {
                    const anchor = event && event.currentTarget; // gone after the await
                    // Re-check against the server first: this row may have been
                    // rendered before the automation picked the job up.
                    const fresh = await getCurrentPolicyForAction(row, {
                      forEdit: true,
                    });
                    if (!fresh) return;
                    // A policy with steps (Reliance / KSHEMA): Proposal details / KYC files / Status
                    const current = { ...row, ...fresh };
                    if (hasStageView(current)) {
                      setEditMenu({ anchor, policy: current });
                      return;
                    }
                    await handleEditPolicy(
                      fresh._id || row._id,
                      fresh.parts?.length ? fresh.parts : row.parts
                    );
                  }}
                >
                  <EditIcon fontSize="small" />
                </AsyncIconButton>
            )}
            {/* Watch the automation run — only offered when this policy
                actually has a video. Re-running a policy replaces its
                recording, so this is always the latest run, and it disappears
                once S3 expires the file after 10 days. */}
            {row?.hasRecording && (
              <Tooltip title="Watch Automation Recording" disableInteractive>
                <span>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setRecordingPolicy({
                        id: row._id || row.policyId || row.id,
                        name: row.fullName || row.firstName || "Policy",
                      })
                    }
                    sx={ACTION_SX}
                  >
                    <VideocamIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {/* History: every attempt and edit for this policy. The job document
                carries its history across edits, so this shows the full story. */}
            {/* hover: a Reliance policy's three steps (proposal / KYC / payment) in a small card; others keep the plain hint */}
            <Tooltip
              title={
                hasStageView(row) ? (
                  <RelianceStagesHoverCard relianceStageView={row.relianceStageView} paymentMethod={row.Paymentmethod} />
                ) : (
                  "View Job History"
                )
              }
              placement="left"
              // only while the pointer is on the History icon - moving onto the card closes it (the owner, 2026-10-27)
              disableInteractive
              arrow={!hasStageView(row)}
              enterDelay={hasStageView(row) ? 250 : 100}
              componentsProps={
                hasStageView(row)
                  ? {
                      tooltip: {
                        sx: {
                          backgroundColor: "#FFFFFF",
                          color: "#101828",
                          border: "1px solid #EAECF0",
                          borderRadius: "12px",
                          boxShadow: "0 12px 24px -6px rgba(16,24,40,0.18), 0 4px 8px -4px rgba(16,24,40,0.08)",
                          p: 1.5,
                          maxWidth: "none",
                        },
                      },
                    }
                  : undefined
              }
            >
              <span aria-label="View Job History">
                <IconButton
                  size="small"
                  onClick={() =>
                    // History opens as its own page now (there is a lot of it): /onlinePolicy/view/history/:policyId
                    handleViewPolicyHistory(row._id || row.policyId || row.id, row.fullName || row.firstName || "Policy")
                  }
                  sx={ACTION_SX}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <AsyncIconButton
              title="View Policy Details"
              color={NAVY}
              sx={ACTION_SX}
              onClick={async () => {
                await getCurrentPolicyForAction(row);
                await handleViewPolicy(row._id || row.policyId || row.id);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </AsyncIconButton>
          </Box>
        ),
      };
    });
    // getCurrentPolicyForAction and the row actions patch the row they acted on
    // rather than reloading the list, so the memo has to be rebuilt from the
    // updated tableData for the new status to reach the screen.
  }, [
    tableData,
    handleViewPolicyErrorLogs,
    handleViewPolicyHistory,
    handleEditPolicy,
    handleViewPolicy,
    retryOnlinePolicy,
    setPolicyHold,
    getCurrentPolicyForAction,
  ]);

  // What the Re-submit dialog lists (null for a policy with no parts, which
  // keeps the original wording).
  const retryPlanView = hasRealParts(retryTarget?.parts)
    ? retryPlan(retryTarget.parts, { paymentLink: !!retryTarget.paidByLink })
    : null;
  const retryPartCount = (retryPlanView?.rerun || []).filter(
    (p) => p.kind !== "withPart"
  ).length;
  const retryButtonText =
    retryPartCount === 1
      ? `Yes, re-submit ${
          retryPlanView.rerun.find((p) => p.kind !== "withPart").label
        }`
      : retryPartCount > 1
        ? `Yes, re-submit ${retryPartCount} parts`
        : "Yes, submit again";

  return (
    <>
      {/* op-form-page: the form's page; on a phone it loses its card (onlinePolicyForm.scss) */}
      {/* The details page has no white frame of its own: it uses the whole window (viewOnlinePolicy.scss). */}
      <Box className={`${isPolicyDetailsView ? "" : Styles.formContainer}${showForm || isEditMode ? " op-form-page" : ""}`}>
        <Box>
          {/* Header Section. Not on the form (New / Edit): the owner, 2026-10-27 - "we are already inside the page";
              the step bar says where you are and the form's Cancel (which asks first) goes back. */}
          {!(showForm || isEditMode || isPolicyDetailsView) && (
          <Grid
            container
            className="Master_Card_Container"
            sx={{ border: "none", pb: "5px" }}
            data-testid="online-policy-header"
          >
            <Grid
              container
              alignItems={"center"}
              className="Master_Card_Sub_Container"
            >
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {(showForm || isEditMode) && (
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleReset}
                      sx={{
                        ...SECONDARY_BUTTON_SX,
                        fontSize: "13px",
                        height: 36,
                      }}
                    >
                      Back
                    </Button>
                  )}
                  <Typography className="Master_Card_Tittle">
                    {isEditMode ? "Edit Online Policy" :
                      isErrorLogsView ? "Policy Issues" :
                        showForm ? "New Online Policy" : "Online Policy"}
                  </Typography>
                </Box>
              </Grid>
              {!isEditMode && !showForm && !isErrorLogsView ? (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  gap={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  {/* Filters toggle. The badge carries the count so a filter
                      left on is visible with the panel closed — otherwise a
                      short list reads as "no policies" rather than "filtered". */}
                  <Button
                    className="Master_Card_Filter_Button w-100"
                    sx={{ width: "102px", height: "40px", gap: "8px" }}
                    startIcon={
                      <Badge
                        badgeContent={activeFiltersCount}
                        color="error"
                        sx={{
                          "& .MuiBadge-badge": {
                            right: 5,
                            top: 3,
                            width: "14px",
                            height: "14px",
                            minWidth: "14px",
                            borderRadius: "50%",
                            fontSize: "8.91px",
                            fontWeight: "600",
                            backgroundColor: "#E50000",
                            color: "#fff",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      >
                        <FilterListIcon sx={{ marginRight: "1.5px" }} />
                      </Badge>
                    }
                    onClick={() => setShowFilter(!showFilter)}
                  >
                    Filters
                  </Button>
                  {/* The super admin cannot create policies (the server refuses too). */}
                  {!superAdmin && (
                    <Button
                      className="Master_Card_create_Button w-100"
                      startIcon={
                        <AddIcon
                          sx={{
                            marginRight: "1.5px",
                          }}
                        />
                      }
                      onClick={() => setShowForm(true)}
                    >
                      {"Create"}
                    </Button>
                  )}
                  <IconButton
                    className="Master_Card_create_Button"
                    sx={{
                      backgroundColor: "#f0f2ff",
                      color: "#667eea",
                      border: "1px solid #667eea",
                      "&:hover": {
                        backgroundColor: "#667eea",
                        color: "white",
                      },
                    }}
                    onClick={handleRefresh}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Grid>
              ) : isErrorLogsView && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  gap={2}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleReset}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#1E3A5F",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#D4AF37",
                        color: "white",
                      },
                    }}
                  >
                    Back to List
                  </Button>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <Divider
                sx={{
                  backgroundColor: "#EAECF0",
                  borderWidth: 1,
                }}
              />
            </Grid>
          </Grid>
          )}

          {/* Main Content */}
          {!showForm &&
            !isEditMode &&
            !isErrorLogsView &&
            !isPolicyDetailsView ? (
            // Table View
            <>
              {/* Filter panel — collapsed until the Filters button is
                  pressed, the same pattern CPAList and BulkUploadedPolicyList
                  use. overflow/zIndex are needed because the dropdowns inside
                  escape this box. */}
              {showFilter && (
                <Box
                  sx={{
                    // A card, not a bare strip. Without the frame the controls
                    // and the Search/Clear buttons float at opposite ends of a
                    // full-width row with nothing tying them together.
                    mx: { xs: 0.5, md: 1 },
                    mb: { xs: 1, md: 2 },
                    p: { xs: 1.25, md: 2 },
                    backgroundColor: "#fff",
                    border: "1px solid #EAECF0",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                    // The company dropdown escapes this box, so it must not be
                    // clipped and must sit above the table.
                    overflow: "visible",
                    position: "relative",
                    zIndex: 100,
                    minHeight: "60px",
                    width: "auto",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#667085",
                      mb: 1.5,
                    }}
                  >
                    Filter policies
                  </Typography>
                  {/* Laid out like every other list in the app
                      (CPAList, BulkUploadedPolicyList): one horizontal strip,
                      no internal header. The action buttons are ours — see the
                      note on hideDefaultButtons below. */}
                  <FilterBox
                    key={filterBoxKey}
                    filterData={filterBoxData}
                    type={"single"}
                    handleSaveAllFilter={handleSaveAllFilter}
                    handleClearAllState={handleClearAllState}
                    handleClearSingleFilter={handleClearSingleFilter}
                    horizontalLayout={true}
                    hideHeader={true}
                    hideInput={true}
                    alwaysOpen={true}
                    autoApply={false}
                    // FilterBox's own Search button calls handleSaveAllFilter —
                    // the same callback it fires on every dropdown change — so
                    // there is no way to tell the two apart. Its buttons are
                    // replaced with ours, which call applyFilters directly.
                    hideDefaultButtons={true}
                    customActionButtons={
                      <>
                        <Button
                          variant="contained"
                          onClick={applyFilters}
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#1E3A5F",
                            borderRadius: "6px",
                            px: 2,
                            "&:hover": { backgroundColor: "#2E5A8F" },
                          }}
                        >
                          Search
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleClearAllState}
                          sx={{
                            textTransform: "none",
                            color: "#344054",
                            borderColor: "#D0D5DD",
                            borderRadius: "6px",
                            px: 2,
                            "&:hover": {
                              borderColor: "#D0D5DD",
                              backgroundColor: "#F9FAFB",
                            },
                          }}
                        >
                          Clear All
                        </Button>
                      </>
                    }
                  />
                  {/* Not a filter: it only colours the rows, so it applies at
                      once and Clear All leaves it alone. */}
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: "1px solid #F2F4F7",
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      columnGap: 2,
                      rowGap: 1,
                    }}
                  >
                    <Box
                      component="label"
                      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                      <Switch
                        size="small"
                        checked={showRowColors}
                        onChange={(e) => toggleRowColors(e.target.checked)}
                        inputProps={{ "aria-label": "Colour rows by status" }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#1E3A5F" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "#1E3A5F",
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#344054", ml: 0.5 }}>
                        Colour rows by status
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        opacity: showRowColors ? 1 : 0.5,
                      }}
                    >
                      {Object.entries(ROW_TONES).map(([key, tone]) => (
                        <Box
                          key={key}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: tone.bg,
                            border: "1px solid #EAECF0",
                            borderLeft: `4px solid ${tone.bar}`,
                          }}
                        >
                          <Box
                            sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: tone.bar }}
                          />
                          <Typography sx={{ fontSize: 12, color: "#344054" }}>
                            {tone.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Search. Applied by the SERVER like the filters above — see the
                  reload effects — so the paging counts stay correct instead of
                  describing an unfiltered set. */}
              <Grid
                container
                sx={{ px: 1, mb: 1, alignItems: "center" }}
                spacing={1}
              >
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search company, policy no, customer, make & model..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        "&:hover fieldset": {
                          borderColor: "#667eea",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#667eea",
                        },
                      },
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={8}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {activeFiltersCount > 0 && (
                    <Chip
                      size="small"
                      label={`${activeFiltersCount} filter${activeFiltersCount > 1 ? "s" : ""} on`}
                      onDelete={handleClearAllState}
                      sx={{
                        backgroundColor: "#f0f2ff",
                        color: "#667eea",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Grid>
              </Grid>

              {/* Table */}
              <Grid container>
                <Grid item xs={12}>
                  {isPhone ? (
                  <PolicyCardList
                    rows={processedTableData}
                    toneOf={showRowColors ? rowToneOf : undefined}
                    loading={openLoader}
                    totalCount={totalCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPage={(next) => setPage(next)}
                    onRowsPerPage={(n) => {
                      setRowsPerPage(n);
                      setPage(0);
                    }}
                  />
                  ) : (
                  <MasterCommonTable
                    columns={columns}
                    data={processedTableData}
                    allData={processedTableData}
                    tableConfig={{
                      isAction: false, // Disable default actions
                      isView: false,
                      isEdit: false,
                      rowSx: showRowColors ? rowToneSx : undefined,
                    }}
                    loading={openLoader}
                    isServerSide={true}
                    tableTotalCount={totalCount}
                    limit={rowsPerPage}
                    tableOutputEvent={(params) => {
                      // A header click arrives here as `sort`, and the sort
                      // effect re-queries from page 1 on its own — so setting
                      // the page here too would fire a second, identical
                      // request.
                      if (params.sort !== undefined) {
                        if (handleTableSort(params.sort)) return;
                      }
                      if (params.page !== undefined) {
                        setPage(params.page - 1);
                      }
                      if (params.limit !== undefined) {
                        setRowsPerPage(params.limit);
                      }
                    }}
                  />
                  )}
                </Grid>
              </Grid>
            </>
          ) : isErrorLogsView ? (
            // Error Logs View
            <ViewOnlinePolicyErrorLogs />

          ) : isPolicyDetailsView ? (
            // Policy Details View
            <ViewOnlinePolicy onBack={handleReset} />
          ) : showForm || isEditMode ? (
            // Form View with Stepper
            <Formik
              key={isEditMode ? `edit-${policyId}` : "create"}
              initialValues={
                isEditMode && editPolicyData
                  ? {
                    // Company + Product Selection + `settings`. Same loader as
                    // handleEditPolicy's setValues (routingFromPolicy) so the
                    // two can never restore different things — they used to
                    // fall back differently, and a policy's TP insurer went
                    // missing depending on which one ran last.
                    ...routingFromPolicy(editPolicyData),
                    ...selectionFromPolicy(editPolicyData),

                    // Basic Policy Details
                    makeModel: editPolicyData.makeModel || "",
                    rtoCityLocation: normalizeRtoCode(editPolicyData.rtoCityLocation),
                    // Saved zone and variant — see the note in the sibling map above.
                    zone: editPolicyData.zone || "",
                    vehicleVariant: editPolicyData.vehicleVariant || "",
                    // Were missing here while the sibling map in
                    // handleEditPolicy restored them, so between mount and that
                    // setValues the form held no region/city at all. The master
                    // re-sync in BasicPolicyDetailsStep corrects the values
                    // themselves — these just stop the fields starting undefined.
                    RTORegion: editPolicyData.RTORegion || "",
                    RTOCity: editPolicyData.RTOCity || "",
                    idv: editPolicyData.idv || "",
                    // See the note in the sibling map above — the IDV field's
                    // disabled state reads straight off modelNotFound, so it
                    // must not start undefined.
                    modelNotFound: editPolicyData.modelNotFound || false,
                    manufacturerSellingPrice:
                      editPolicyData.manufacturerSellingPrice || "",
                    idvRangeFrom: editPolicyData.idvRangeFrom || "",
                    idvRangeTo: editPolicyData.idvRangeTo || "",
                    idvByHand: !!editPolicyData.idvByHand,
                    discount: editPolicyData.discount !== undefined && editPolicyData.discount !== null ? String(editPolicyData.discount) : "",
                    manufacturingYear: editPolicyData.manufacturingYear || "",
                    manufacturingMonth:
                      editPolicyData.manufacturingMonth || "",
                    purchaseDate: parseMongoDate(editPolicyData.purchaseDate),
                    registrationDate: parseMongoDate(
                      editPolicyData.registrationDate
                    ),
                    zeroDepreciation:
                      editPolicyData.zeroDepreciation || false,
                    zeroDepreciationPercentage:
                      editPolicyData.zeroDepreciation
                        ? (typeof editPolicyData.zeroDepreciationPercentage !== "undefined" && editPolicyData.zeroDepreciationPercentage !== null
                          ? editPolicyData.zeroDepreciationPercentage
                          : 0)
                        : 0,
                    tppdRestrict: editPolicyData.tppdRestrict || false,
                    paCover: editPolicyData.paCover || false,

                    paCoverAmount: editPolicyData.paCoverAmount || "",
                    notPaCoverReason: editPolicyData.notPaCoverReason || "",
                    nomineeName: editPolicyData.nomineeName || "",
                    nomineeGender: editPolicyData.nomineeGender || "",
                    nomineeRelation: editPolicyData.nomineeRelation || "",
                    nomineeDob: parseMongoDate(editPolicyData.nomineeDob),
                    // nomineeAge and paCoverYears were missing here while the
                    // sibling map in handleEditPolicy restored them — National
                    // policies opened through this path lost both.
                    nomineeAge: editPolicyData.nomineeAge || "",
                    paCoverYears: editPolicyData.paCoverYears || 1,
                    // A saved policy's PA term counts as chosen — see the sibling map in handleEditPolicy.
                    _paYearsChosen: true,
                    otherRelationName: editPolicyData.otherRelationName || "",
                    appointeeName: editPolicyData.appointeeName || "",
                    // KSHEMA only — see the note in the sibling map above.
                    nomineeType: editPolicyData.nomineeType || "individual",
                    nomineeShare: editPolicyData.nomineeShare ?? "100",
                    // Financier — see the matching restore in handleEditPolicy.
                    hasFinancier: editPolicyData.hasFinancier || false,
                    financierType: editPolicyData.financierType || "",
                    financierName: editPolicyData.financierName || "",
                    financierAddress: editPolicyData.financierAddress || "",
                    existingTpPolicyNumber: editPolicyData.existingTpPolicyNumber || "",
                    existingTpInsurer: editPolicyData.existingTpInsurer || "",
                    existingTpStartDate: parseMongoDate(editPolicyData.existingTpStartDate),
                    existingTpEndDate: parseMongoDate(editPolicyData.existingTpEndDate),
                    fullName: editPolicyData.fullName || editPolicyData.firstName || "",
                    surname: editPolicyData.surname || editPolicyData.lastName || "",
                    kshemaSendSurname: editPolicyData.kshemaSendSurname === true,
                    dateOfBirth: parseMongoDate(editPolicyData.dateOfBirth),
                    gender: editPolicyData.gender || "",
                    proposerTitle: editPolicyData.proposerTitle || titleFromGender(editPolicyData.gender) || "",
                    _titleChosen: !!editPolicyData.proposerTitle,
                    fatherName: editPolicyData.fatherName || "",
                    // See the note in the sibling map above — without this the
                    // saved Aadhaar number is dropped on edit.
                    aadharNumber: editPolicyData.aadharNumber || "",
                    flatDoorNo: editPolicyData.flatDoorNo || "",
                    floorNo: editPolicyData.floorNo || "",
                    buildingName: editPolicyData.buildingName || "",
                    blockName: editPolicyData.blockName || "",
                    roadStreetLane: editPolicyData.roadStreetLane || "",
                    areaAndLocality: editPolicyData.areaAndLocality || "",
                    state: editPolicyData.state || "",
                    country: editPolicyData.country || "India",
                    pincode: editPolicyData.pincode || "",
                    email: editPolicyData.email || "",
                    mobileNumber: editPolicyData.mobileNumber || "",
                    engineNumber: editPolicyData.engineNumber || "",
                    chassisNumber: editPolicyData.chassisNumber || "",
                    // Existing uploads (stored objects, not File objects) so
                    // the form can show what is already on the policy.
                    aadharCard: editPolicyData.aadharCard || null,
                    panCard: editPolicyData.panCard || null,
                  }
                  : { ...policyInitialValues, ...customerDetailsInitialValues }
              }
              validationSchema={combinedValidationSchema}
              innerRef={formRef}
            >
              {({ errors, touched, values, setFieldValue, validateForm, setTouched, ...formikProps }) => (
                // op-policy: the form's card layout (onlinePolicyForm.scss); op-form is shared with the User page
                <Form className={`${Styles.formContent} op-form op-policy`}>
                  {/* Editing a policy where some parts are already bought:
                      say what will and will not be re-submitted. */}
                  {isEditMode && <CompletedPartsNotice policy={editPolicyData} />}

                  {/* Stepper, in a card of its own like the sections under it */}
                  <Box className="op-policy-stepper">
                    <Stepper activeStep={activeStep} alternativeLabel sx={STEPPER_SX}>
                      {formSteps.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  {/* Step 0: Basic Policy Details (now includes premium summary inline) */}
                  {activeStep === 0 && (
                    <BasicPolicyDetailsStep
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      selectedMakeModel={selectedMakeModel}
                      setSelectedMakeModel={setSelectedMakeModel}
                      selectedCombo={selectedCombo}
                      setSelectedCombo={setSelectedCombo}
                      filteredVehicleData={filteredVehicleData}
                      filterVehicleData={filterVehicleData}
                      onTppdRestrictChange={handleTppdRestrictChange}
                      onZeroDepreciationChange={handleZeroDepreciationChange}
                      formik={formikProps}
                      calculationResult={calculationResult}
                      showCalculationResult={showCalculationResult}
                      setShowCalculationResult={setShowCalculationResult}
                      savedSnapshot={isEditMode ? savedSnapshot : null}
                      releaseSaved={releaseSaved}
                    />
                  )}

                  {/* Step 1: Customer, Address & Vehicle Details */}
                  {activeStep === 1 && (
                    <CustomerDetailsStep
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                    />
                  )}

                  {/* Step 2: Preview */}
                  {activeStep === 2 && isEditMode && showPayerChoice && superAdmin && (
                    <Typography sx={{ mt: 2, fontSize: 13, color: "#475467" }}>
                      Any amount for this edit is charged to the policy&apos;s own admin / creator.
                    </Typography>
                  )}
                  {activeStep === 2 && isEditMode && showPayerChoice && !superAdmin && (
                    <EditPayerChoice
                      creatorName={creatorName}
                      editorName={sessionStorage.getItem("name") || ""}
                      value={chargeTo}
                      onChange={(next) => {
                        setChargeToState(normalizeChargeTo(next));
                        setFieldValue("chargeTo", normalizeChargeTo(next), false);
                      }}
                    />
                  )}
                  {activeStep === 2 && (
                    <PreviewStep
                      policyData={policyData}
                      walletChecks={walletChecks}
                      describeWalletCheck={describeWalletCheck}
                      customerValues={values}
                      calculationResult={calculationResult}
                      selectedMakeModel={selectedMakeModel}
                      selectedCombo={selectedCombo}
                      renderEditFooter={
                        isEditMode && hasRealParts(editPolicyData)
                          ? ({ newSplit }) => (
                              <ResubmitPartsPanel
                                parts={editPolicyData.parts}
                                savedPolicy={editPolicyData}
                                values={values}
                                newSplit={newSplit}
                                vehicle={selectedMakeModel}
                                paymentLink={isPaymentLink(editPolicyData)}
                                buyAgain={!isPaymentLink(editPolicyData)}
                                overrides={values._partOverrides || {}}
                                // UI-only fields; validation is not needed for them.
                                onOverridesChange={(next) =>
                                  setFieldValue("_partOverrides", next, false)
                                }
                                onKeysChange={(keys) => {
                                  setFieldValue("resubmitParts", keys, false);
                                  setBoughtAgain(
                                    boughtAgainKeys(editPolicyData.parts, keys, {
                                      paymentLink: isPaymentLink(editPolicyData),
                                    })
                                  );
                                }}
                              />
                            )
                          : undefined
                      }
                    />
                  )}

                  {/* Navigation Buttons */}
                  <FormNavigationButtons
                    activeStep={activeStep}
                    handleBack={handleBack}
                    handleReset={handleReset}
                    handleNext={handleStepNext}
                    handleNextStep={handleNextStep}
                    saveCustomerDetails={handleFinalSubmit}
                    values={values}
                    validateForm={validateForm}
                    setTouched={setTouched}
                    touched={touched}
                    isEditMode={isEditMode}
                    showCalculationResult={showCalculationResult}
                    updateLabel={
                      isEditMode &&
                      hasRealParts(editPolicyData) &&
                      Array.isArray(values.resubmitParts)
                        ? resubmitLabel(values.resubmitParts, editPolicyData.parts).button
                        : null
                    }
                  />
                </Form>
              )}
            </Formik>
          ) : null}
        </Box>
      </Box>
      {/* <Loader open={openLoader} /> */}

      {/* Job history */}
      {/* Hold on a RUNNING policy: "Stop the automation?" with the step it is on */}
      <StopRunDialog
        policy={stopConfirm}
        onCancel={() => setStopConfirm(null)}
        onConfirm={async (p) => {
          await setPolicyHold(p._id || p.id, true);
          setStopConfirm(null);
        }}
      />

      {/* Edit on a Reliance / KSHEMA row: what to change */}
      <Menu
        // On a phone the button it would hang from was in the actions sheet, which has gone: the middle of the screen instead.
        {...(isPhone
          ? {
              anchorReference: "anchorPosition",
              anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 },
              transformOrigin: { vertical: "center", horizontal: "center" },
            }
          : {
              anchorEl: editMenu ? editMenu.anchor : null,
              anchorOrigin: { vertical: "bottom", horizontal: "right" },
              transformOrigin: { vertical: "top", horizontal: "right" },
            })}
        open={!!editMenu}
        onClose={() => setEditMenu(null)}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 250, boxShadow: "0 8px 24px rgba(16,24,40,0.14)" } }}
      >
        {[
          {
            key: "proposal",
            icon: <DescriptionOutlinedIcon fontSize="small" />,
            title: "Proposal details",
            hint: "The full edit form - a new proposal is made",
            go: async (p) => handleEditPolicy(p._id, p.parts?.length ? p.parts : []),
          },
          {
            key: "kyc",
            icon: <BadgeOutlinedIcon fontSize="small" />,
            title: "KYC files",
            hint: "New Aadhaar / PAN - only the KYC runs again",
            go: async (p) => setKycFilesFor(p),
          },
          {
            key: "status",
            icon: <TuneRoundedIcon fontSize="small" />,
            title: "Status",
            hint: "Set the steps by hand",
            go: async (p) => setStatusFor(p),
          },
          // Two insurers only, once the TP part is finished: the TP policy's number and dates, which Reliance OD waits for
          ...(editMenu && existingTpModeOfPolicy(editMenu.policy) === "optional" && tpDetailsOpen(editMenu.policy)
            ? [
                {
                  key: "tpPolicy",
                  icon: <ShieldOutlinedIcon fontSize="small" />,
                  title: "TP policy details",
                  hint: "TP policy number and dates - Reliance OD waits for them",
                  go: async (p) => setTpPolicyFor(p),
                },
              ]
            : []),
        ].map((item) => (
          <MenuItem
            key={item.key}
            data-testid={`edit-menu-${item.key}`}
            onClick={async () => {
              const p = editMenu && editMenu.policy;
              setEditMenu(null);
              if (p) await item.go(p);
            }}
            sx={{ py: 1, gap: 0.5 }}
          >
            <ListItemIcon sx={{ color: "#1E3A5F", minWidth: 34 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.title}
              secondary={item.hint}
              primaryTypographyProps={{ fontSize: "13.5px", fontWeight: 600, color: "#1E3A5F" }}
              secondaryTypographyProps={{ fontSize: "11.5px" }}
            />
          </MenuItem>
        ))}
      </Menu>

      <KycFilesDialog
        policy={kycFilesFor}
        onClose={() => setKycFilesFor(null)}
        onChanged={(job) => kycFilesFor && patchRowFromResponse(kycFilesFor._id || kycFilesFor.id, job)}
      />

      <StageStatusDialog
        policy={statusFor}
        onClose={() => setStatusFor(null)}
        onChanged={(job) => statusFor && patchRowFromResponse(statusFor._id || statusFor.id, job)}
      />

      <TpPolicyDialog
        policy={tpPolicyFor}
        onClose={() => setTpPolicyFor(null)}
        onChanged={(job) => tpPolicyFor && patchRowFromResponse(tpPolicyFor._id || tpPolicyFor.id, job)}
      />

      {/* Automation screen recording */}
      <JobRecordingDialog
        open={!!recordingPolicy}
        onClose={() => setRecordingPolicy(null)}
        policyId={recordingPolicy?.id}
        customerName={recordingPolicy?.name}
      />

      {/* Re-submit confirmation */}
      <RetryPolicyDialog
        target={retryTarget}
        planView={retryPlanView}
        statusText={retryTarget?.statusLabel || statusLabel(retryTarget?.jobStatus)}
        resubmitPaid={!!retryTarget?.paidByLink && COMPLETED_JOB_STATUSES.includes(retryTarget?.jobStatus)}
        buttonText={retryButtonText}
        submitting={retrySubmitting}
        onCancel={() => setRetryTarget(null)}
        onConfirm={async () => {
          if (!retryTarget) return;
          setRetrySubmitting(true);
          try {
            await retryOnlinePolicy(retryTarget.id);
          } finally {
            // Always close: retryOnlinePolicy toasts its own failure, and
            // leaving the dialog open on error just traps the user.
            setRetrySubmitting(false);
            setRetryTarget(null);
          }
        }}
      />

      {/* Payment Selection Dialog */}
      <Dialog
        open={openPaymentDialog}
        onClose={() => setOpenPaymentDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <Box sx={{ p: 2.5, textAlign: "left", fontFamily: FONT }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, fontSize: "16px", color: NAVY, fontFamily: FONT, lineHeight: 1.3 }}>
            Payment Method
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#475467", fontSize: "12.5px", lineHeight: 1.6, fontFamily: FONT }}>
            {walletChecks.length > 0 ? (
              <>
                {paymentMode === "link"
                  ? "Motor premium is paid by link; the following Brisk PA amount is deducted from the Brisk wallet:"
                  : isEditMode
                    ? 'Choosing "Use Wallet Balance" reconciles the Company Wallet to this edit — refunding what was already charged, then re-charging from the edited data:'
                    : 'Choosing "Use Wallet Balance" deducts the following from the Company Wallet:'}
                {walletChecks.map((r, i) => (
                  <React.Fragment key={r.company || i}>
                    <br />
                    <strong style={{ color: "#B42318", fontWeight: 700 }}>
                      {r.company || "Unresolved"}:{" "}
                      {isEditMode
                        ? r.delta <= 0
                          ? `no additional charge (${r.labels.join(" + ")})`
                          : `₹${r.delta} more (${r.labels.join(" + ")})`
                        : `₹${r.amount} (${r.labels.join(" + ")})`}
                    </strong>
                  </React.Fragment>
                ))}
              </>
            ) : (
              paymentMode === "link"
                ? "Motor premium is paid by link; no Brisk PA wallet charge applies."
                : "Choose how this policy is being paid for."
            )}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {/* Wallet Option */}
            <Box
              sx={{
                px: 1.75,
                py: 1.5,
                border: `1.5px solid ${paymentMode === "wallet" ? NAVY : "#EAECF0"}`,
                borderRadius: "12px",
                cursor: "pointer",
                bgcolor: paymentMode === "wallet" ? "#EEF3F9" : "white",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "border-color 0.15s, background-color 0.15s",
                "&:hover": { borderColor: paymentMode === "wallet" ? NAVY : "#C9D7E8" },
              }}
              onClick={() => handlePaymentModeChange("wallet")}
            >
              <input
                type="radio"
                checked={paymentMode === "wallet"}
                onChange={() => handlePaymentModeChange("wallet")}
                style={{ width: 18, height: 18, accentColor: NAVY, flexShrink: 0, cursor: "pointer" }}
              />
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, fontFamily: FONT, fontSize: "13.5px" }}>Use Wallet Balance</Typography>
                {walletChecks.length > 0 ? (
                  walletChecks.map((r, i) => (
                    <Typography
                      key={r.company || i}
                      variant="caption"
                      color={r.sufficient ? "text.secondary" : "error"}
                      sx={{ display: "block", fontFamily: FONT, fontSize: "12px", lineHeight: 1.5 }}
                    >
                      {r.company || "Unresolved insurer"}: {describeWalletCheck(r)}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT, fontSize: "12px" }}>
                    No amount to check for this policy
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Link Option */}
            <Box
              sx={{
                px: 1.75,
                py: 1.5,
                border: `1.5px solid ${paymentMode === "link" ? NAVY : "#EAECF0"}`,
                borderRadius: "12px",
                cursor: "pointer",
                bgcolor: paymentMode === "link" ? "#EEF3F9" : "white",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                transition: "border-color 0.15s, background-color 0.15s",
                "&:hover": { borderColor: paymentMode === "link" ? NAVY : "#C9D7E8" },
              }}
              onClick={() => handlePaymentModeChange("link")}
            >
              <input
                type="radio"
                checked={paymentMode === "link"}
                onChange={() => handlePaymentModeChange("link")}
                style={{ width: 18, height: 18, accentColor: NAVY, flexShrink: 0, cursor: "pointer" }}
              />
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: NAVY, fontFamily: FONT, fontSize: "13.5px" }}>Send Payment Link</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT, fontSize: "12px" }}>
                  {walletChecks.length > 0
                    ? "Motor premium is paid by link; Brisk PA is charged to the Brisk wallet."
                    : "Send a link to the customer; no Company Wallet charge applies."}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5, display: "flex", gap: 1.25 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setOpenPaymentDialog(false)}
              sx={{ ...SECONDARY_BUTTON_SX, height: 40 }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handlePaymentSubmit}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null}
              sx={{ ...PRIMARY_BUTTON_SX, height: 40 }}
            >
              {creating ? (isEditMode ? "Updating…" : "Creating…") : isEditMode ? "Proceed & Update" : "Proceed & Create"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* A wallet is short at Create: cancel, or save it on hold (nothing charged, the automation waits). */}
      <ConfirmBox
        open={Boolean(holdOffer)}
        title="Hold"
        content="Not enough wallet balance. Save this policy on hold until the money is added?"
        subContent={holdOffer ? `${holdOffer.message}. Nothing is charged now; press Remove hold on the policy after the top up.` : ""}
        confirmButton="Hold until money is added"
        setOpenConfirmBox={(open) => !open && setHoldOffer(null)}
        Function={createOnHold}
      />

      {/* Remove hold of a policy waiting for wallet money: what will be charged, then the automation starts. */}
      <ConfirmBox
        open={Boolean(chargeConfirm)}
        title="Charge"
        content="Charge the wallet and start this policy?"
        subContent={chargeConfirm ? `${requiredText(chargeConfirm) || "The policy's wallets"} will be charged now. If a wallet is still short nothing is charged and it stays on hold.` : ""}
        confirmButton="Charge & start"
        setOpenConfirmBox={(open) => !open && setChargeConfirm(null)}
        Function={async () => {
          const row = chargeConfirm;
          setChargeConfirm(null);
          if (row) await setPolicyHold(row._id, false);
        }}
      />
    </>
  );
};

export default OnlinePolicy;
